/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
const en = {
  common: {
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    save: "Save",
    delete: "Delete",
    actions: "Actions",
    sendRequest: "Send request",
    id: "Id",
    name: "Name",
    serialNo: "Serial number",
    serialNos: "Serial numbers",
    deviceType: "Device Type",
    activate: "Activate",
    deactivate: "Deactivate",
    back: "Back",
    forward: "Forward",
    submit: "Submit",
    upload: "Upload",
    selectAll: "Select all",
    selected: "Selected",
    status: "Status",
    threePhase: "Three Phase",
    inverter: "Inverter",
    battery1: "Battery #1",
    battery2: "Battery #2",
    battery3: "Battery #3",
    battery4: "Battery #4",
    battery5: "Battery #5",
    battery6: "Battery #6",
    battery7: "Battery #7",
    battery8: "Battery #8",
    warrantyStartDate: "Warranty start date",
    warrantyEndDate: "Warranty end date",
    extendedWarranty: "Extended warranty",
    standardWarranty: "Standard warranty",
    loginFail: "Failed to login!",
    login: "Login",
    logout: "Logout",
    noData: "No data found!",
    error: "Error",
  },
  notAvailable: "N/A",
  notImplemented: "Not implemented",
  // TODO: Consider deriving device display names from the product registry.
  // The registry already has displayName per product. Would need to also add
  // plural forms and initials to the registry, or generate i18n entries from it.
  devices: {
    libbi: {
      singular: "libbi",
      plural: "libbis",
      initials: "l",
    },
    eddi: {
      singular: "eddi",
      plural: "eddis",
      initials: "e",
    },
    eddiPlus: {
      singular: "eddi+",
      plural: "eddis+",
      initials: "e+",
    },
    harvi: {
      singular: "harvi",
      plural: "harvis",
      initials: "h",
    },
    zappi: {
      singular: "zappi",
      plural: "zappis",
      initials: "z",
    },
    zappi1: {
      singular: "zappi 1",
      plural: "zappi 1",
      initials: "z1",
    },
    zappi2: {
      singular: "zappi 2",
      plural: "zappi 2",
      initials: "z2",
    },
    zappi2ti: {
      singular: "zappi 2",
      plural: "zappi 2",
      initials: "z2",
    },
    rapidZappi: {
      singular: "zappi GLO",
      plural: "zappi GLO",
      initials: "zg",
    },
    zappi3: {
      singular: "zappi 3",
      plural: "zappi 3",
      initials: "z3",
    },
    rapidZappiPlus: {
      singular: "zappi GLO+",
      plural: "zappi GLO+",
      initials: "zv",
    },
    hub: {
      singular: "hub",
      plural: "hubs",
      initials: "h",
    },
    vHub: "vHub",
  },
  components: {
    tryAgain: {
      text: "An error occured while loading data",
      label: "Try again",
    },
    dataError: {
      message:
        "WARNING: There's a data related ERROR on this page! \nWe're displaying the available data where possible but beware that some data may be inaccurate or missing. \nIf this issue persists, please contact the dev team with a screenshot and a link to this page so they can investigate.",
    },
    errorMessage: {
      header: "Something went wrong",
      content:
        "We apologize for the inconvenience. If the issue persists please contact your line manager.",
      reload: "Reload the page",
    },
  },
  mainPage: {
    dashboard: "Dashboard",
    firmware: "Firmware",
    dsr: "Demand Side Response",
    fleetApi: "Fleet API",
    thirdPartyApi: "3rd Party API",
    ocpp: "OCPP",
    mqtt: "MQTT",
    gridpayNl: "Gridpay NL",
    users: "Users",
    servers: "Servers",
    customers: "Customers",
    installers: "Installers",
    myportal: "myportal",
    hubs: "Hubs",
    accessManagement: "Access Management",
    securityMessageTitle: "Your connection to admin portal is not secure!",
    securityMessage:
      "To enhance the security and privacy of our customers admin portal will soon be moved internal only and you will lose access. Please contact your local IT team to make sure a VPN link is setup.",
  },
  hubsPage: {
    monitor: "Auto-Refresh",
    monitored: "Refreshing ...",
    hubFilters: {
      generalFilter: {
        blocked: "Blocked",
        registered: "Registered",
        notRegistered: "Not Registered",
        online: "Online",
        firmwareUpToDate: "Firmware Up-to-Date",
        oldFirmware: "Old Firmware",
        beta: "Beta",
        aggregator: "Aggregator",
      },
    },
    hubTable: {
      serial: "Serial #",
      hubType: "Hub Type",
      firmwareVersion: "Firmware Version",
      beta: "Beta",
      relatedDevices: "Related Devices",
      isOnline: "Last Seen",
      isBlocked: "Blocked",
      isNotBlocked: "Unblocked",
      registered: "Registered",
      noDataText: "No hubs to show at the moment",
      deregister: "deregister",
      linkLiveView: "Customer Live View",
      unlinkLiveView: "Unlink Live View",
    },
    hubDeregisterPopUp: {
      title: "Warning",
      text: "Are you sure you want to deregister hub {{hub}} ?",
    },
    deviceDeletePopUp: {
      title: "Warning",
      text: "Do you want to delete the {{deviceName}} with serial number {{deviceSerialNo}} from hub {{hubSerialNo}}?",
    },
    deviceReconnectPopUp: {
      title: "Warning",
      text: "Do you want to reconnect the {{deviceName}} with serial number {{selectedDeviceSerialNo}}?",
    },
    downloadBootloaderPopUp: {
      title: "Warning",
      text: "Are you sure you want to Force Bootloader Download (Command=17) this {{deviceName}} with serial number {{deviceSerialNo}}? Downloading the bootloader carries a risk of permanently disabling the unit.",
    },
    installBootloaderPopUp: {
      title: "Warning",
      text: "Are you sure you want to Install the Bootlader (Command=19) on this {{deviceName}} with serial number {{deviceSerialNo}}?",
    },
    clearCommandsPopUp: {
      title: "Clear Initial Commands",
      text: "Are you sure you want to Clear all Initial Commands of this {{deviceName}} with serial number {{deviceSerialNo}}?",
      success:
        "Deleted {{deletedFromPostgresCount}} PostGres commands and {{deletedFromRedisCount}} Redis commands.",
    },
    forceRebootPopUp: {
      title: "Warning",
      text: "Are you sure you want to Force Reboot (Command=18) this {{deviceName}} with serial number {{deviceSerialNo}}?",
    },
    forceBackgroundDownloadPopUp: {
      title: "Warning",
      text: "Are you sure you want to Download the {{firmwareType}} on this {{deviceName}} with serial number {{deviceSerialNo}}?",
      defaultFirmwareDownload: "Firmware (Command=20)",
      forceDownloadLibbiArm: "Inverter ARM Firmware (Command=560)",
      forceDownloadLibbiBms: "Inverter BMS Firmware (Command=562)",
      forceDownloadLibbiDsp: "Inverter DSP Firmware (Command=561)",
    },
    installFirmwarePopUp: {
      title: "Warning",
      text: "Are you sure you want to Install the {{firmwareType}} on this {{deviceName}} with serial number {{deviceSerialNo}}?",
      defaultFirmwareInstall: "Firmware (Command=20)",
      forceInstallLibbiArm: "Inverter ARM Firmware (Command=576)",
      forceInstallLibbiBms: "Inverter BMS Firmware (Command=578)",
      forceInstallLibbiDsp: "Inverter DSP Firmware (Command=577)",
    },

    deviceG100ResetPopUp: {
      title: "G100 Reset",
      text: "Are you sure you want to send a G100 {{resetType}} Reset command to this {{deviceName}} with serial number {{serialNo}}?",
    },
    superUserModal: {
      title: "Access Customer myaccount Page",
      customerEmail: "Customer Email",
      hasPermission:
        "I confirm that I have been granted verbal permission from the customer to access their myaccount page and data.",
      error: "Unable to connect to customer live view!",
    },
    blockHubModal: {
      title: "Block hub",
      content: "Please enter a reason for blocking hub {{hubSerial}}.",
      reason: "Reason",
      error: "Unable to block hub",
    },
  },
  hubDetailsPage: {
    successMessages: {
      deregisterHub: "Hub successfully deregistered",
      blockHub: "Hub successfully blocked",
    },
    thirdPartyIntegrations: "3rd Party Integrations",
    powerFactorTooltip:
      "Power factors are calculated by admin portal and based on L1 voltage, as such they should be considered as a guide only. Power factor is only calculated if power is more than +/-100W.",
    copiedToClipboard: "{{value}} copied to clipboard.",
    exportConfig: {
      buttonLabel: "Download Config",
      confirmTitle: "Export Ecosystem Configuration",
      confirmMessage:
        "This will download a PDF containing the full ecosystem configuration including security codes. Continue?",
      download: "Download PDF",
      partialWarning:
        "Config unavailable for some devices ({{devices}}). PDF generated with available data.",
      error: "Failed to export ecosystem configuration",
    },
    errorMessages: {
      deregisterHub: "Couldn't deregister hub",
      blockHub: "Couldn't block hub",
      unlinkSuperUser: "Couldn't unlink super user from this account!",
      noData: "No hub data was found",
    },
    hub: {
      id: "Id",
      serialNo: "Serial No",
      updatedAt: "Updated At",
      regPassword: "Registration PW",
      uptime: "Uptime",
      lastHeard: "Hub Last Heard",
      lastLoadControl: "Last Load Control",
      deviceNotFoundTitle: "Device Not Found",
      deviceNotFoundError:
        "The selected {{deviceName}} device with serial {{deviceSerial}} was not found. This usually suggests that the selected device is not part of this hub or the URL is malformed.",
      internetConnection: {
        ethernetAndWifi: "Ethernet and Wifi",
        ethernet: "Ethernet",
        wifi: "Wifi",
        none: "None",
      },
      activity: {
        title: "Activity",
        hubLastHeard: "Hub Last Heard",
        serverLastHeard: "Server Last Heard",
        appLastActive: "App Last Active",
      },
      hubMonitoring: {
        title: "Device Monitoring",
        enabled: "Enabled",
        disabled: "Disabled",
        start: "Start",
        stop: "Stop",
        extend: "Extend",
        extended: "Extended by",
        monitoringChanged: "Device monitoring was {{state}}.",
        monitoringExtended: "Device monitoring was extended by {{days}} days.",
        confirmTitle: "Device monitoring",
        confirmContent:
          "Do you want to {{state}} device monitoring for hub {{hubSerial}}?",
        extendTitle: "Extend device monitoring",
        extendDays: "Number of days:",
      },
      firmwarePanel: {
        title: "Firmware",
        currentVersion: "Current version",
        availableFirmwareVersions: "Available firmware versions",
        noData: "No data available",
      },
      version: "Firmware Version",
      networkId: "Network ID",
      noDevices: "No Devices",
      iPAddress: "IP Address",
      port: "Port",
      aggregator: "Aggregator",
      overview: {
        registered: "Registered",
        encrypted: "Encrypted",
        regPassword: "Registration Password",
        currentVersion: "Current Version",
        createdAt: "First heard",
        udpPort: "UDP Port",
        networkID: "Network ID",
        totalGrid: "Total Grid",
        totalGeneration: "Total Generation",
        lastAppAccess: "Last App Access",
        noLinkedDevices: "No. Linked Devices",
        masterRaw: "Master",
        notRegistered: "Not Registered",
        australia: "Australia",
        radioChannel: "Radio Channel",
        loadControlUpdatedAt: "Load Control Updated",
        setLoad: "Set Load",
        network: "Network",
        appHistory: "Mobile App History",
        lastActive: "Last Active",
        enableAll: "Enable All",
        usingSSO: "Using SSO",
        appVersion: "App Version",
        mobileDeviceType: "Mobile Device Type",
        mobileDeviceName: "Mobile Device Name",
      },
      deviceOverviewPanel: {
        noServerSelected: "No Server Selected",
        hubServerOverride: "Hub Server Override",
      },
      deviceConfigs: {
        noEditPermissionTitle: "Edit config permission",
        noEditPermissionMessage:
          'Your account does NOT have permission to edit device configs. Please ask an admin to give you "Device Config - Edit" rights.',
        exitEditTitle: "Exit edit mode",
        exitEditMessage:
          "Sure you want to exit edit mode? All changes will be discarded.",
        exitWarningTitle: "Edit mode warning",
        exitWarningMessage:
          "Navigating away while in edit mode, will discard all changes.",
        saveChangesTitle: "Save changes",
        saveChangesMessage:
          "Are you sure you want to submit the following config changes to the device?",
        commandFailedTitle: "Failed to submit command",
        commandFailedMessage:
          "The following errors were found while attempting to submit the command. Please correct them and try again.",
        commandSubmitted: "Command submitted",
        commandInProgress:
          "A command is in progress... submitting further commands is disabled until it completes.",
        configUnavailable: "Configuration is unavailable for this device.",
      },
      deviceConfigNavbar: {
        basicSettings: "Basic Settings",
        advancedSettings: "Advanced Settings",
        engineeringMode: "Engineering Mode",
        services: "Services",
        relaysAndSensors: "Relays & Sensors",
        expansionCard: "Expansion Card",
        ocpp: "OCPP Settings",
        ocppChargingProfiles: "OCPP Charging Profiles",
        pinSyncStatus: "Pin Sync",
        chargingSlots: "Super Scheduler",
      },
      deviceConfigSubpanels: {
        required: "Field required",
        errorMessageMustBeWithinRelationBoundary:
          "Must be {{relation}} {{boundary}}",
        secondsSuffix: "SECONDS",
        preconditioning: {
          title: "Preconditioning",
          precon: "Preconditioning",
          preconEnergy: "Preconditioning Energy",
          preconEnergySuffix: "kWh",
        },
        managedMode: {
          title: "Managed Mode",
          managedModeEnabled: "Managed Mode enabled",
        },
        timersLimits: {
          title: "Timers / Limits",
          hotTime: "Hot Time",
        },
        chargeSettings: {
          title: "Charge Settings",
          timeBoost: "Time Boost",
          timeBoostNo: "No",
          timeBoostStart: "Start",
          timeBoostDuration: "Duration",
          timeBoostDays: "Days",
          ecoPlus: "ECO+",
          smartCharging: "Smart Charging",
          manualBoost: "Manual Boost",
          smartBoost: "Smart Boost",
          boostEnergySuffix: "kWh",
          boostBy: "by",
          minGreenLevel: "Minimum Green Level",
          minGreenLevelSuffix: "%",
          startStopDelay: "Start / Stop Delay",
          startDelay: "Start Delay",
          stopDelay: "Stop Delay",
          plugInCharge: "Plug-in Charge",
          defaultMode: "Default Mode",
          defaultSmartChargeMode: "Default Smart Charge Mode",
          ecoModePhaseSetting: "Eco Mode Phase Setting",
          libbiDrainEnabled: "Libbi drain enabled",
          boostBatteryLimit: "Boost battery limit",
          exportLimit: "Export limit",
          limits: "Limits",
          minStateOfCharge: "Min state of charge",
          maxStateOfCharge: "Max state of charge",
          stateOfChargeSuffix: "% normalised",
          schedules: "Schedules",
          target: "Target",
          targetTime: "Time",
          targetKwh: "kWh",
          rate: "Rate",
          mode: "Mode",
          chargeFromGrid: "Charge From Grid Status",
          enums: {
            libbiMode: {
              BALANCE: "Balance",
              CAPTURE: "Capture",
              CHARGE: "Charge",
              DRAIN: "Drain",
              MATCH: "Match",
              STOP: "Stop",
            },
            ecoModePhaseSetting: {
              ECO_SINGLE_AND_ECO_PLUS_SINGLE:
                "ECO: 1PH, ECO+: 1PH (1 PHASE in App)",
              ECO_THREE_AND_ECO_PLUS_THREE:
                "ECO: 3PH, ECO+: 3PH (3 PHASE in App)",
              ECO_SINGLE_AND_ECO_PLUS_AUTO:
                "ECO: 1PH, ECO+: AUTO (AUTO in App)",
              ECO_THREE_AND_ECO_PLUS_AUTO: "ECO: 3PH, ECO+: AUTO (AUTO in App)",
              tooltip:
                "Note that the App displays only 3 values as described in this dropdown's labels. When AUTO is selected via the App, it translates to ECO 3ph ECO+ AUTO.",
            },
          },
        },
        timeAndDate: {
          title: "Time & Date",
          timezone: "Timezone",
          dateFormat: "Date format",
          dstOn: "DST On",
          updateFromCloud: "Update from Cloud",
          countryCode: "Country Code",
        },
        lockFunction: {
          title: "Lock Function",
          lockCode: "Lock Code",
          lockCodeFormatErrorMsg: "5 digits, each between 1-4",
          lockEVPluggedIn: "Lock - EV Plugged in",
          lockEVUnplugged: "Lock - EV Unplugged",
          lockTimeout: "Lock Timeout",
          lockTimeoutLowerBoundaryErrorMsg: "Cannot be lower than 10",
          lockTimeoutUpperBoundaryErrorMsg: "Cannot be more than 99",
          lockAutohide: "Lock Autohide",
          chargeWhenPINLocked: "Charge when PIN Locked",
          lockTimeoutSuffix: "sec",
          lockedNow: "Locked Now",
        },
        displaySound: {
          title: "Display & Sound",
          sound: "Sound",
          language: "Language",
          icons: "Icons",
          backlight: "BackLight",
          backlightLowerBoundaryErrorMsg: "Must be > 0",
          backlightUpperBoundaryErrorMsg: "Must be < 60",
          contrast: "Contrast",
          contrastSuffix: "10 - 24",
          libbiContrastSuffix: "40 - 60",
          eddiContrastSuffix: "40 - 60",
          contrastLowerBoundaryErrorMsg: "Must be > 10",
          contrastUpperBoundaryErrorMsg: "Must be < 24",
          setBuzzer: "Set Buzzer",
          required: "Field required",
          languages: {
            de: "German",
            en: "English",
            es: "Spanish",
            fr: "French",
            it: "Italian",
            nl: "Dutch",
            sv: "Swedish ",
          },
        },
        rgb: {
          title: "RGB LED",
          preview: "Preview",
          r: "R",
          g: "G",
          b: "B",
          fault: "Fault",
          greenCharging: "Green Charging",
          mixedCharging: "Mixed Charging",
          dirtyCharging: "Dirty Charging",
          chargeComplete: "ChargeComplete",
          stateB: "State B",
          ledBrightness: "LED Brightness",
          percentageSuffix: "%",
          rgbLowerBoundaryErrorMsg: "Must be >= 0",
          rgbUpperBoundaryErrorMsg: "Must be <= 100",
        },
        general: {
          title: "General",
          silentMode: "Silent mode",
          oldPowerRegOverride: "Legacy power control",
          modbusTimeout: "Modbus Timeout",
          silentModeTooltip:
            "Uses slightly more energy but results in a quieter system by driving a relay with a constant voltage rather than a PWM signal",
        },
        supplyGrid: {
          title: "Supply Grid",
          enums: {
            phaseOrPhaseRotation: {
              L1: "L1",
              L2: "L2",
              L3: "L3",
            },
            batteryMode: {
              BATTERY_MODE_NONE: "None",
              BATTERY_MODE_AVOID_CHARGE: "Avoid Charge",
              BATTERY_MODE_AVOID_DISCHARGE: "Avoid Discharge",
              BATTERY_MODE_AVOID_BOTH: "Avoid Both",
              BATTERY_MODE_WITHIN_GEN: "Within Gen",
            },
            earthing: {
              TTOrTN: "TT/TN",
              IT: "IT",
            },
            phaseReturn: {
              PHASE_RETURN_N: "N",
              PHASE_RETURN_L1: "L1",
              PHASE_RETURN_L2: "L2",
              PHASE_RETURN_L3: "L3",
              PHASE_RETURN_MODULO: "Modulo",
            },
          },
          device: {
            title: "Device",
            phaseOrPhaseRotation: "Phase/Phase Rotation",
            phaseReturn: "Phase Return",
            phaseReturnSuffix: "Connected to Neutral Terminal",
            deviceLimit: "Device Limit",
            chargeLimit: "Charge Limit",
            dischargeLimit: "Discharge Limit",
            neutralLimitError: "Must be 0, or between 13 and 100",
            earthing: "Earthing",
            gridPowerLimit: "Grid Power Limit",
            minimumExport: "Export Threshold",
            minimumExportTime: "Response Delay",
            masterLostThreshold: "Master Lost Threshold",
            masterLostDetection: "Master Lost Detection",
            masterLostThresholdLowerBoundaryErrorMsg: "Cannot be lower than 0",
            masterLostThresholdUpperBoundaryErrorMsg: "Cannot be more than 31",
            neutralLimit: "Neutral Limit",
            neutralLimitLowerBoundaryErrorMsg: "Cannot be lower than 13",
            neutralLimitUpperBoundaryErrorMsg: "Cannot be more than 100",
            powerLimit: "Power Limit",
            powerLimitLowerBoundaryErrorMsg: "Cannot be lower than 6",
            powerLimitUpperBoundaryErrorMsg: "Cannot be more than 60",
            required: "Field required",
          },
          network: {
            title: "Network",
            exportMargin: "Export Margin",
            gridLimit: "Grid Limit",
            gridLimitTooltip:
              "The device monitoring the GridPower CT is {{deviceName}}. When the grid is being monitored by a harvi the max Grid Limit should not exceed 65A. For every other hub device the max Grid Limit is 100A.",
            gridLimitError: "Must be 0, or between 13 and 100",
            gridPowerLimitError: "Must be 0, or between 3 and 30",
            batteryMode: "Battery Mode",
            netPhases: "Net Phases",
            exportLimit: "Export Limit",
            exportLimitLowerBoundaryErrorMsg: "Cannot be lower than 1",
            exportLimitUpperBoundaryErrorMsg: "Cannot be more than 100",
            required: "Field required",
            gridSubdivide: "Subdivision of country grid code",
            pvInputMode: "PV MPPT Input Mode",
          },
        },
        ctConfig: {
          title: "CT Config",
          internalLoadCTs: "Internal Load CTs",
          externalCTs: "External CTs",
          virtualCTs: "Virtual CTs",
          g100: "G100",
          readingNow: "Reading Now",
          power: "Power",
          powerFactor: "Power Factor",
          ctGroup: "CT Group",
          groupLimit: "Group Limit",
          groupLimitError: "Must be 0 (Off), or between 1 and 60",
          enums: {
            undefined: "NoN",
            null: "NoN",
            NONE: "None",
            GridPower: "Grid",
            GenerationPower: "GP",
            StoragePower: "SP",
            GenerationAndBattery: "GB",
            InternalLoadCTPower: "IL",
            MonitorCTPower: "MN",
            ACBattery: "AC",
            DCBattery: "DC",
            DCPV: "DCPV",
            HeatPump: "HP",
          },
          enumsLong: {
            undefined: "NoN",
            NONE: "None",
            GridPower: "Grid Power",
            HeatPump: "Heat Pump",
            GenerationPower: "Generation Power",
            StoragePower: "Storage Power",
            GenerationAndBattery: "Generation & Battery",
            InternalLoadCTPower: "Internal Load CT Power",
            MonitorCTPower: "Monitor CT Power",
            ACBattery: "AC Battery",
            DCBattery: "DC Battery",
            DCPV: "DC PV",
          },
          primary: "CT Primary",
          ctDetection: "CT Detection",
          threePhaseMonitoring: "3 Phase Monitoring",
          gridCTNoPowerTooltip:
            "No power detected on Grid CT. This MAY indicate it has not been installed correctly.",
        },
        eSenseInput: {
          title: "eSense Input",
          enums: {
            ESENSE_DISABLED: "Disabled",
            ESENSE_BOOST: "Boost",
            ESENSE_BOOST_ENABLED: "Boost Enabled",
            ESENSE_CURTAILMENT: "Curtailment",
            ESENSE_STOP: "Stop",
            ESENSE_BOOST_ENABLE: "Boost Enabled",
            ESENSE_HEATER_ENABLED: "Heater Enabled",
            ESENSE_HEATER_ENABLE: "Heater Enabled",
            ESENSE_RELAY_CONTROL: "Relay Control",
          },
          boostMode: {
            ESENSE_1: "Heater 1",
            ESENSE_2: "Heater 2",
            ESENSE_1_AND_2: "Heater 1 & 2",
          },
        },
        compatibilityMode: {
          title: "Compatibility Mode",
          compatibilityModeActive: "Active",
          compatibilityModeMinPWM: "Min PWM",
          compatibilityModePowerFactor: "Power Factor",
          chargeCompleteDelay: "End Charge Delay",
          chargeCompleteDelaySuffix: "SECONDS",
          autoPhaseSwitching: "Auto Phase Switching",
        },
        settingsPasscode: {
          title: "Advanced Settings Passcode",
          eddiTitle: "Installer Settings",
        },
        protectionBoard: {
          title: "Protection Board",
          pwm: "PWM",
          relay1: "Relay 1",
          relay2: "Relay 2",
          mainSwitch: "Main Switch",
          lockSolenoid: "Lock Solenoid",
          peRelay: "PE Relay",
          weldedRelayCheckDelay: "Welded Relay Check Delay",
        },
        randomDelayStart: {
          title: "Charge Delay",
          label: "Start Charge Delay (Max)",
        },
        priority: {
          title: "Priority",
          heater: "Heater",
          checkPeriod: "Check Period",
          priorityUpperBoundaryErrorMsg: "Must be <= {{boundary}}",
          priorityLowerBoundaryErrorMsg: "Must be >= {{boundary}}",
        },
        boostTimer: {
          title: "Boost Timer",
          title2: "Timers",
          heater: "Heater {{number}}",
          relay: "Relay {{number}}",
          flagT: "T",
          flagE: "e",
          temp: "Temp",
          flags: "Flags",
          enums: {
            NONE: "None",
            TANK_1: "Tank 1",
            TANK_2: "Tank 2",
            RADIATOR: "Radiator",
            UNDERFLOOR: "Underfloor",
            POOL: "Pool",
            BUFFER: "Buffer",
          },
        },
        relays: {
          title: "Relays",
          relay1: "Relay 1",
          relay2: "Relay 2",
          onThreshold: "On Threshold",
          offThreshold: "Off Threshold",
          includeDiverted: "Include diverted",
          minimumOnTime: "Minimum On Time",
          minimumOffTime: "Minimum Off Time",
          heating: "Heating",
          boostIcon: "Boost Icon",
          relayModes: {
            DISABLED: "Disabled",
            EXPORT: "Export",
            IMPORT: "Import",
            HEATING: "Heating",
            BOOST_TIMER: "Boost",
            FAULT: "Fault",
            FOLLOW_RELAY: "Follow",
          },
          boostType: {
            Battery: "Battery",
            Boiler: "Boiler",
            HeatPump: "Heat Pump",
            NotBoostable: "Not Boostable (not selectable)",
          },
        },
        esense: {
          title: "eSense Input",
          boostMode: "Boost Mode",
          heaterEnableMode: "Heater Enable Mode",
          boostEnableMode: "Boost Enable Mode",
          relayControlMode: "Relay Control Mode",
        },
        temperatures: {
          title: "Temperatures",
          temp: "Temperature",
          hysteresis: "Hysteresis",
          setPoint: "Set Point",
          relay1: "Relay 1",
          relay2: "Relay 2",
        },
        libbiCombinedInverterUpdate: {
          title: "Trigger Download of Combined Inverter Firmware",
          text:
            "This process will cause firmware to be downloaded and installed" +
            "\n" +
            "\n" +
            "Note: This process will take between 5-20 minutes.",
          highBaudRate:
            "Standard Version will install the latest inverter specific firmware version",
          serverVersions:
            "Factory Version will restore the orginal factory firmware versions",
          confirmTitle: "Warning - Inverter firmware download and install",
          confirmText:
            "Are you sure you want to download and install {{firmwareTypeTSEName}} firmware to libbi inverters?",
          tseNameHighBaudRate: "STANDARD VERSION",
          tseNameServerVersion: "FACTORY VERSION",
        },
        forceReadConfig: {
          title: "Force a Device Read Config",
          text: "This command will request a config read from the device.",
          mainConfig: "Main config",
          rfidConfig: "RFID config",
          chargingSlotsConfig: "Charging slots (all 84 slots)",
          chargingSlotsQueued:
            "Reading all charging slots from the device. Progress appears on the Super Scheduler tab.",
          confirmTitle: "Confirm device read config",
          confirmText:
            "Are you sure you want to force a {{configStructureID}} read from the device with serial number {{selectedDeviceSerial}}?",
        },
        setZappiMode: {
          title: "Set Zappi Mode",
          text: "Send a set Zappi mode command to device with serial number {{selectedDeviceSerial}}.",
          commandMode: "Command mode",
          boostOn: "Boost on",
          targetTime: "Target time",
          kWh: "kWh",
          confirmTitle: "Confirm set Zappi mode",
          confirmText:
            "Are you sure you want to change the zappi mode on device with serial number {{selectedDeviceSerial}}?",
        },
      },
    },
    eddi: {
      deviceType: "Eddi",
      id: "Id",
      serialNo: "Serial Number",
      deviceInstance: "Device Instance",
      updatedAt: "Last Seen",
      lastHeard: "Eddi Last Heard",
      currentVersion: "Firmware version",
      priority: "Priority",
      voltage: "Voltage",
      frequency: "Frequency",
      chargeEnergy: "Charge Energy",
      heaterType1: "Heater Type 1",
      heaterType2: "Heater Type 2",
      deviceConfigTime: "Device Config Time",
      ctData: {
        id: "Id",
        ctNumber: "CT Number",
        type: "Type",
        current: "Current",
      },
      ptData: {
        id: "Id",
        ptNumber: "PT Number",
        temp: "Temperature",
        setPoint: "Set Point",
        hyst: "Hyst",
        absent: "Absent",
      },
    },
    libbi: {
      chargeEnergy: "Charge Energy",
      batteryPower: "Battery Power",
      libbiPower: "Libbi Power",
      solarPower: "Solar Power",
      controllerTemp: "Controller Temp",
      inverterTemp: "Inverter Temp",
      batteryTemp: "Battery Temp",
      batteryRadTemp: "Battery Rad Temp",
      ambientTemp: "Ambient Temp",
      chargePercentage: "Charge Percentage",
      normalisedChargePercentage: "Normalised Charge Percentage",
      chargeRemaining: "Charge Remaining",
      batteryCapacity: "Battery Capacity",
      maxInverterCapacity: "Inverter Size",
      chargeRate: "Charge Rate",
      dischargeRate: "Discharge Rate",
      currentMode: "Current Mode",
      serverConfigTime: "Server Config Time",
      deviceConfigTime: "Device Config Time",
      solarConnected: "Solar Connected",
      backupConnected: "Backup Connected",
      controllerFirmware: "Controller Firmware",
      inverterFirmware: "Inverter Firmware",
      batteryCycles: "Battery {{batteryNo}} Cycles To Date",
      lastUpdate: "Last Update",
      gridCTPhaseNumberFitted: "Local Grid CT {{ctNo}}",
      controllerSerialNo: "Controller Serial No",
      inverterSerialNo: "Inverter Serial No",
      battery1SerialNo: "Battery 1 Serial No",
      battery2SerialNo: "Battery 2 Serial No",
      battery3SerialNo: "Battery 3 Serial No",
      battery4SerialNo: "Battery 4 Serial No",
      battery5SerialNo: "Battery 5 Serial No",
      battery6SerialNo: "Battery 6 Serial No",
      battery7SerialNo: "Battery 7 Serial No",
      battery8SerialNo: "Battery 8 Serial No",
      wizardState: "Wizard State",
      calibrationChargeComplete: "Calibration Charge",
      csipAusExportLimit: "CSIP Export Limit",
      csipAusImportLimit: "CSIP Import Limit",
      csipAusGenerationLimit: "CSIP Generation Limit",
      csipAusLoadLimit: "CSIP Load Limit",
      csipAusActive: "CSIP Active",
      csipAusDeEnergise: "CSIP De-Energise",
      csipAusDisconnect: "CSIP Disconnect",
    },
    deviceHealthCheck: {
      overallPassed: "All health checks passed",
      overallFailed: "{{failed}} out of {{total}} health checks failed!",
      checkType: "Check Type",
      categories: "Categories",
      status: "Status",
      tips: "Tips",
      type: {
        hasGridCt: "Has grid CT",
        isOnline: "Is online",
        hasSufficientFwv: "Has sufficient firmware",
      },
      fix: {
        hasGridCt:
          "Ensure a grid CT is properly installed and configured on the device.",
        isOnline:
          "Check the device's network connection and verify it can communicate with the server.",
        hasSufficientFwv: "Update the device firmware to the latest version.",
      },
    },
    chargingSlots: {
      title: "Super scheduler",
      managedMode: "Managed mode",
      managedModeOn: "On",
      managedModeOff: "Off",
      readConfig: "Read config…",
      notManagedNotice:
        "Managed mode is off. Firmware only accepts slot writes when managed mode is on or OCPP is operative, and only acts on slots in those modes. Stored slots will run again if either mode is re-enabled.",
      storedCopyNotice:
        "These are the slots we last read from the device or wrote to it. The device reports only when its slot table changed, never what changed, so a read is the only way to confirm the two agree.",
      commandFailed: "The command could not be sent",
      awaitingDevice:
        "Waiting for the device to acknowledge — the stored slots change only once it does",
      deviceConfirmed:
        "The device acknowledged and the stored slots have moved",
      noConfirmation:
        "No acknowledgement from the device yet. The write may have been refused, and the firmware gives no reason code — try a read to see what it holds",
      showAllSlots: "Show all {{max}} slots ({{empty}} empty)",
      showSlotsInUse: "Show only slots in use",
      startHintViewerZone:
        "The device stores slot times in UTC and schedules on UTC alone. Its own timezone has not been read, so these are shown in your browser timezone ({{timeZone}}) — the device's clock may differ.",
      startHint:
        "The device stores slot times in UTC and schedules on UTC alone; they are shown here converted to its timezone ({{timeZone}}), so a repeating slot's local time moves when the clocks change.",
      clearTitle: "Clear slot {{slotId}}",
      clearMessage:
        "The slot is written with no charge rate. It stays on the device but will not charge. This is one flash write.",
      tiles: {
        slotsInUse: "Slots in use",
        ofMax: "of {{max}}",
        shouldBeRunning: "Should be running",
        nextSlot: "Next slot",
        slotNumber: "Slot {{slotId}}",
        none: "None",
        reservedNote:
          "Slot 84 is where gridpay puts its priority slot — by convention, not by firmware",
        calculatedNote:
          "Calculated from slot data, not reported by the device.",
        restingModeManaged:
          "No slot covers now. Managed mode forces the charge mode on power up to Eco+ and the minimum green level to 100%, so the device rests in surplus-only Eco+",
        restingMode:
          "No slot covers now, so the device rests in its charge mode on power up: {{mode}}",
        restingModeUnknown:
          "No slot covers now. The device then rests in its charge mode on power up, which has not been read from this device",
        nextSlotNote: "Slot {{slotId}} • {{mode}} • calculated",
      },
      timeline: {
        title: "Schedule timeline",
        rangeLabel: "Timeline range",
        rangeHours: "± {{hours}} h",
        rangeDays: "± {{days}} d",
        resolvedLane: "Predicted",
        tip: {
          heading: "Slot {{slotId}} • {{mode}}",
          restingHeading: "No slot • resting in {{mode}}",
          window: "Stretch",
          priority: "Priority",
          repeats: "Repeats",
          slotDuration: "Slot duration",
          preemptor: "slot {{slotId}} (priority {{priority}})",
          whyWins:
            "Highest priority slot covering this stretch, so the device runs it",
          whyRuns: "Nothing of higher priority overlaps here, so this one runs",
          whyPreempted:
            "Pre-empted by {{slots}} — firmware displaces an active slot only for a strictly higher priority",
          whyReserved:
            "Slot 84 is excused every time check, so it stays eligible until plug-off and overrides every lower priority",
          whyExpired:
            "Stored on the device but already ended, and it does not repeat, so firmware skips it",
          whyRestingUnknown:
            "No slot covers this stretch. The device falls back to its charge mode on power up, which has not been read from this device",
        },
        restingUnknown: "its own mode",
        resolvedFootnote:
          "The window is centred on now and reaches the same distance either side, so overnight charging stays in view however late in the day it is. The top lane is what the device is predicted to do: for every stretch, the slot the firmware would be running, with the uncovered stretches showing what it rests in instead. All of it is our arithmetic — the device never reports which slot it picked.",
        priorityLane: "Priority {{priority}}",
        overridingLane: "Priority {{priority}} • overrides",
        preempted: "pre-empted by a higher priority slot",
        reservedLegend: "Slot 84 • gridpay priority",
        untilPlugOff: "until plug-off",
        barLabel: "{{slotId}} • {{mode}}",
        barTitle: "Slot {{slotId}} • {{mode}} • {{duration}} • {{repeats}}",
        footnote:
          "One lane per priority the device actually holds a slot on, highest first. Faded stretches are where a higher priority slot pre-empts this one — the firmware only displaces an active slot for a strictly higher priority, so equal priorities leave the incumbent running. Slot 84 spans the whole range because time is irrelevant to it: it stays eligible until plug-off and overrides every lower priority while it is there.",
      },
      table: {
        title: "Stored slots",
        slot: "Slot",
        start: "Start (device local)",
        end: "End",
        duration: "Duration",
        mode: "Mode",
        priority: "Priority",
        priorityHint:
          "0 to 15, higher wins. Firmware only displaces a running slot for a strictly higher priority",
        max: "Max",
        min: "Min",
        threePhase: "3ph",
        repeats: "Repeats",
        repeatsHint:
          "Slots repeating until plug-off have no meaningful start or end, and plug-off is only honoured on slot 84, which holds the gridpay priority slot by convention — editing it will fight managed mode.",
        yes: "Yes",
        no: "No",
        ended: "ended",
        overrunsRepeat: "overruns repeat",
        overrunsRepeatTitle:
          "This slot runs for longer than the gap between its repeats, so the device runs it continuously rather than repeating it. Something other than this portal wrote it.",
        reservedSlot: "priority slot",
        details: "Details",
        hide: "Hide",
        edit: "Edit",
        clear: "Clear",
        noSlots: "No charging slots stored for this device",
        detail: {
          flashOffset: "Slot id / flash offset",
          byte: "byte {{byte}}",
          startEpoch: "Start (UTC epoch seconds)",
          durationSeconds: "Duration (seconds)",
          unit: "Unit",
          maxChargePerPhase: "Max charge per phase",
          minChargePerPhase: "Min charge per phase",
          energyTarget: "Energy target",
          socLimits: "SoC limits (low – high)",
          updatedAt: "Stored copy updated",
          never: "Unknown",
          flags: "Flags",
          flagsNote:
            "Zappi firmware reads only Smart regs override. Eco and Temp are an eddi boost-timer concept in the same struct.",
          storedNotRead:
            "Stored on the device but never read by Zappi firmware.",
        },
      },
      edit: {
        title: "Edit slot {{slotId}}",
        write: "Write slot to device",
        changes: "Changes",
        endBeforeStart: "End must be after start",
        watts: "Watts",
        tenthAmps: "Amps (tenths)",
        priorityHint: "0 to 15 — higher wins",
        longerThanRepeat:
          "A slot cannot run for longer than the gap between repeats. The device only tracks the current and previous {{repeat}} window, so it would run continuously instead of repeating.",
        recurringInUtc:
          "The device schedules slots in UTC and never applies its own timezone or daylight saving, so a repeating slot keeps the same UTC time. Its local time will move by an hour when the clocks change.",
        writeBudget:
          "Every save is a flash write. The device refuses more than 24 writes in any rolling 60 minutes, does not tell us how many it has used — writes from OCPP profiles and managed mode count too — and a refused write fails without a reason code.",
        notWritable:
          "Managed mode is off and OCPP may not be operative, so the device is likely to refuse this write.",
        reservedSlot:
          "Slot 84 is the reserved boost and managed mode slot. Writing it may fight whatever owns managed mode on this device.",
        field: {
          startLocal: "Start (device local)",
          endLocal: "End (device local)",
          chargingSlotMode: "Mode",
          priority: "Priority",
          unit: "Rate unit",
          maxCharge: "Max charge",
          minCharge: "Min charge (Eco+ floor)",
          recurring: "Repeats",
          operate3ph: "Three phase",
          smartRegsOverride: "Smart regs override",
        },
      },
    },
    devicePinSync: {
      noDataText: "No data to show at the moment",
      noPinsConfigured: "No pins configured",
      friendlyName: "Friendly Name",
      pinStatus: "Status",
      syncStatus: "Sync Status",
      syncButton: "Sync Pins",
      summary: {
        overallInSync:
          "All pins are synchronized between intended configuration and device",
        overallNotInSync:
          "Pin configuration mismatch detected between intended and device state",
        syncPending: "Pin synchronization is in progress, please wait...",
        syncStatusUnknown:
          "Sync status is unknown. This configuration was created before device-level tracking. Please sync to update.",
      },
      status: {
        inSync: "In Sync",
        missingFromDevice: "Missing from Device",
        extraOnDevice: "Extra on Device",
      },
      statusDetail: {
        notOnDevice:
          "This pin is configured in Myaccount but not present on the device",
        notIntended:
          "This pin exists on device but is not in the Myaccount configuration",
        synchronized: "Pin is correctly synchronized",
      },
    },
    deviceStatusPanelDetails: {
      serialNo: "Serial No",
      deviceNetworkID: "Device Network ID",
      internet: "Active Network Interface",
      parentHubAddress: "Parent Hub Address",
      g100Status: "G100 Lockout State",
      g100Unlock: "Unlock",
      date: "Date",
      time: "Time",
      MQTTLastHeard: "MQTT Last Heard",
      lastCommand: "Last Command",
      retries: "Retries",
      commandNo: "Command No",
      commandSeqNo: "Sequence",
      currentVersion: "Current Version",
      currentVersionWarning:
        "Other devices in this ecosystem are not on the same major version so the system may display unpredictable behaviour.",
      armVersion: "ARM Version",
      dspVersion: "DSP Version",
      dsp2Version: "DSP2 Version",
      bmsVersion: "BMS Version",
      bmsVersionAnomalous: "BMS Version Anomalous",
      lastArmVersion: "Last ARM Version",
      lastDspVersion: "Last DSP Version",
      lastInverterFwInstallType: "Last Inverter Firmware Install Type",
      lastBaudRateInstallType: "Last Baud Rate Install Type",
      threePhaseInverter: "Three phase inverter",
      melibVersion: "Melib Version",
      melinkVersion: "Melink Version",
      stubLoaderVersion: "Stubloader Version",
      bootLoaderVersion: "Bootloader Version",
      createdAt: "First Heard",
      diverted: "Diverted",
      voltage: "Voltage",
      voltage1: "Voltage 1",
      voltage2: "Voltage 2",
      voltage3: "Voltage 3",
      voltageWarning:
        "The supported device voltage range is 202.4v to 257.6v. The grid voltage range is 216.2 to 253v",
      frequency: "Frequency",
      totalGrid: "Total Grid",
      totalGen: "Total Generation",
      priority: "Device Priority",
      state: "State",
      heatersAvailable: "Heaters Available",
      microprocessorTI: "microprocessorTI",
      wifiFirmwareVersion: "Wifi Firmware Version",
      serverConfigTime: "Server Config Time",
      deviceConfigTime: "Device Config Time",
      harviCTs: {
        ctNumber: "CT{{number}} Number",
        type: "CT{{number}} Type",
        phase: "CT{{number}} Phase",
        current: "CT{{number}} Current",
        power: "CT{{number}} Power",
        powerFactor: "CT{{number}} Power Factor",
      },
      subMenu: {
        status: "Status",
        healthCheck: "Health Check",
        history: "History",
        commandHistory: "Command History",
        connectionHistory: "Connection History",
        historyData: "Minute History",
        chargingHistory: "Charging History",
        dsr: "DSR",
        ocpp: "OCPP",
        integrations: "Integrations",
        errorsAndWarnings: "Errors & Warnings",
        g100Events: "G100/2 Events",
        rigData: "Rig Data",
        commissioning: "Commissioning",
      },
      subMenuButtons: {
        downloadBootloader: "Download Bootloader",
        forceReboot: "Reboot Device",
        forceBackgroundDownload: "Download Firmware",
        downloadInverterFirmwareBMS: "Download Inverter Firmware [BMS]",
        downloadInverterFirmwareARM: "Download Inverter Firmware [ARM]",
        downloadInverterFirmwareDSP: "Download Inverter Firmware [DSP]",
        installFirmware: "Install Firmware",
        installInverterFirmwareBMS: "Install Inverter Firmware [BMS]",
        installInverterFirmwareARM: "Install Inverter Firmware [ARM]",
        installInverterFirmwareDSP: "Install Inverter Firmware [DSP]",
        installCombinedInverterFirmware: "Install Combined Inverter Firmware",
        installBootloader: "Install Bootloader",
        forceReadConfig: "Read Device Config",
        setZappiMode: "Set Zappi Mode",
        clearCommands: "Clear Initial Commands",
        deleteDevice: "Delete Device",
        hardDeleteDevice: "Hard Delete Device!",
      },
    },
    deviceCommandHistory: {
      initiatedAt: "Initiated At",
      type: "Type",
      state: "State",
      initiator: "Initiator",
      lastSent: "Command Last Sent",
      commandParameters: {
        title: "Command Details",
        subTitle: "Parameters:",
        boostTimer: "Boost Timer",
        heaterPriorityCheckPeriodMinutes: "Heater PriorityCheck Period Minutes",
        lockFunction: "Lock Function",
        minGreenLevel: "Min Green Level",
        setMode: "Set Mode",
        setPriority: "Set Priority",
        newConfigTime: "New Config Time",
        noCommands: "There are no parameters for this command.",
      },
      commandExplain: {
        title: "Explained:",
        zappiMode: "Set charging mode to {{mode}}",
        manualBoost: "Set a manual boost to {{kWh}}kWh",
        smartBoost: "Set a smart boost to {{kWh}}kWh by {{time}}",
        cancelBoost: "Cancel boost",
        zappiModeEnum: {
          0: "Don't Change",
          1: "Fast",
          2: "Eco",
          3: "Eco+",
          4: "Stop",
        },
      },
      commandClient: {
        title: "Client Info:",
        noCommands: "There are no client data for this command.",
      },
      commandState: {
        initial: "Initial",
        inProgress: "In Progress",
        failed: "Failed",
        success: "Success",
        null: "N/A",
        commandNotSent: "Command was not sent",
      },
      commandType: {
        setModeEddi: "Set Mode Eddi",
        setModeZappi: "Set Mode Zappi",
        setLock: "Set Lock",
        setPriority: "Set Priority",
        setEddiBoost: "Set Eddi Boost",
        readConfig: "Read Main Config",
        readRfidConfig: "Read RFID Config",
        writeConfigSetHeaterPriority: "Write Config Set Heater Priority",
        writeConfigSetBoostTime: "Write Config Set Boost Time",
        writeConfigSetZappiBoostTime: "Write Config Set Zappi Boost Time",
        writeConfigSetEddiBoostTime: "Write Config Set Eddi Boost Time",
        writeConfigSetLibbiBoostTime: "Write Config Set Libbi Boost Time",
        writeConfigSetMinGreenLevel: "Write Config Set Min Green Level",
        writeZappi2OcppConfig: "Write Config Ocpp",
        writeChargingSlot: "Write Charging Slot",
        writeZappi2LiteOcppConfig: "Write Zappi2 Lite Ocpp Config",
        downloadBootloader: "Download Bootloader",
        forceReboot: "Force Reboot",
        forceBackgroundDownload: "Force Firmware Download",
        forceInstallApp: "Force Firmware Install",
        forceInstallLibbiArm: "Force Libbi Inverter ARM Firmware Install",
        forceInstallLibbiBms: "Force Libbi Inverter BMS Firmware Install",
        forceInstallLibbiDsp: "Force Libbi Inverter DSP Firmware Install",
        forceDownloadLibbiArm: "Force Libbi Inverter ARM Firmware Download",
        forceDownloadLibbiBms: "Force Libbi Inverter BMS Firmware Download",
        forceDownloadLibbiDsp: "Force Libbi Inverter DSP Firmware Download",
        forceInstallBootloader: "Force Bootloader Install",
        g100Reset: "G100 Reset",
        cancelChargeDelay: "Cancel Charge Delay",
        dsrSetFrequency: "DSR Set Frequency",
        dsrSetLoad: "DSR Set Load",
        setModeLibbi: "Set Mode Libbi",
        writeLibbiConfig: "Write Libbi Config",
        writeZappi2Config: "Write Zappi2 Config",
        writeRapidZappiConfig: "Write RapidZappi Config",
        writeEddiConfig: "Write Eddi Config",
        writeEddiPlusConfig: "Write Eddi Plus Config",
        writeRapidZappiPlusConfig: "Write RapidZappi Plus Config",
        writeZappi3Config: "Write Zappi3 Config",
        writeConfigSetBatteryDischarge: "Write Config Set Battery Discharge",
        readG100Events: "Read G100/2 Events",
        writeZappi2AuthPinConfig: "Write PIN Config",
        writeRfidConfig: "Write RFID Config",
        libbiCombinedInverterUpdate: "Libbi Combined Inverter Update",
        null: "N/A",
      },
      commandInitiatorService: {
        appApi: "Application API",
        meLinkAppApi: "Application API",
        trpcAppApi: "New Application API",
        adminApi: "Admin API",
        hubReceiver: "Hub Receiver",
        commandApi: "Command API",
        openapi: "3rd Party API",
        ocpp: "OCPP",
        dsrApi: "DSR API",
        phoenix: "Phoenix",
        other: "Other",
        kaluzaApi: "Kaluza API",
        fleetApi: "Fleet API",
        firmwareOrchestrator: "Firmware Update Orchestrator",
        configService: "Config Service",
        installers: "Installer Service",
        null: "N/A",
      },
    },
    deviceHistory: {
      connection: "Connection",
      mqtt: "MQTT",
      command: "Command",
      minute: "Minute",
      charging: "Charging",
      firmware: "Firmware",
      smartScheduler: "Smart Scheduler",
    },
    deviceConnectionHistory: {
      connectionTime: "Connection Time",
      disconnectionTime: "Disconnection Time (approximate)",
      connectionDuration: "Duration (dd:hh:mm:ss)",
      stillActive: "Still Active",
    },
    mqttConnectionHistory: {
      timestamp: "Timestamp",
      isConnected: "Is Connected",
      disconnectReason: "Disconnect Reason",
    },
    historyData: {
      intro:
        "Download or graph telemetry data recorded by the device every minute.",
      minute: "Minute",
      hour: "Hour",
      halfHour: "Half Hour",
      date: "Date",
      startDate: "Start Date",
      endDate: "End Date",
      csv: "CSV",
      json: "JSON",
      plot: "Plot",
      download: "Download",
      showDataGaps: "Show data gaps",
      dataPointsCount:
        "Showing <bold>{{current}}</bold> of <bold>{{total}}</bold> data points",
      seriesName: "Series Name",
      min: "Min",
      max: "Max",
      average: "Average",
      total: "Total",
      unit: "Unit",
      yAxis: "Y Axis",
      noData: "No data found for the selected date.",
      selectSeries: "Select the series below to plot.",
      downloadFileError: "Failed to download file!",
      formattingError: "Failed to format data!",
      errors: {
        requiredField: "Required field",
        dateFormat: "The date must be in the format DD/MM/YYYY",
      },
      downloadLimits: {
        minute: "Minute resolution is limited to 1 day per download.",
        hour: "Hour resolution is limited to a maximum of 180 days per download.",
        halfHour:
          "Half hour resolution is limited to a maximum of 90 days per download.",
      },
    },
    chargingHistory: {
      intro:
        "Explore and download device charging history (aka charge logs or charge sessions).",
      pluggedIn: "Plugged In",
      pluggedOut: "Plugged Out",
      chargeStart: "Charge Start",
      chargeEnd: "Charge End",
      chargeDuration: "Charge Duration",
      totalCharge: "Total Charge",
      greenCharge: "Green Charge",
      authType: "Auth Type",
      isBusiness: "Is Business",
      noData: "No charging history found for the date range selected.",
    },
    firmwareHistory: {
      intro: "History of firmware jobs associated with this device.",
      queued: "Queued",
      retryAttempts: "Retry Attempts",
      noData: "No firmware job history found for this device.",
    },
    smartSchedulerHistory: {
      created: "Created",
      createdTimeZone: "Created (Time Zone)",
      enabled: "Enabled",
      active: "Active",
      readyBy: "Ready By",
      target: "Target",
      timezone: "Timezone",
      addButton: "Smart Schedule",
      modal: {
        title: "Smart Scheduler",
        description:
          "Configure automated gridpay charging schedules by setting a target energy amount (kWh) and a completion deadline (Ready by Local Time).",
        localTime: "Ready by Local Time",
        failedToSave: "Failed to save smart scheduler.",
        successfullySaved: "Smart schedule saved successfully.",
      },
    },
    deviceIntegrations: {
      integratorId: "Integrator ID",
      integratorName: "Integrator Name",
      integratorType: "Type",
      typeFleet: "Fleet",
      typeThirdParty: "3rd party",
      emptyTable: "No integrators have access to this device",
      eventType: "Event Type",
      createdAt: "Created Date",
      updatedAt: "Updated Date",
      integrationsTab: "Integrations",
      historyTab: "History",
      webhookHistory: {
        title: "Webhook Event Log",
        timestamp: "Timestamp",
        eventType: "Event Type",
        clientId: "Client ID",
        clientType: "Client Type",
        deviceSerialNo: "Serial number",
        endpoint: "Endpoint",
        httpStatusCode: "Status",
        durationMs: "Duration",
        attemptNumber: "Attempt",
        requestBody: "Request Body",
        responseBody: "Response Body",
        signature: "Signature",
        messageId: "Message ID",
        eventId: "Event ID",
        errorMessage: "Error",
        noData: "No webhook events found for this device.",
        noDataForClient:
          "No webhook deliveries to this integrator in the last 7 days.",
        retention:
          "Deliveries are kept for 7 days. Every attempt is listed, including retries.",
        timeout: "Timeout",
        filterFrom: "From",
        filterTo: "To",
        clearFilters: "Clear",
        allEventTypes: "All",
      },
    },
    deviceG100Events: {
      pendingCommands:
        "You may need to Clear Initial Commands before fetching events.",
      fetchEvents: "Fetch G100/2 Events",
      fetchingEvents: "Waiting for G100/2 events...",
      fetchingError: "Unable to fetch G100/2 events.",
      events: "{{eventsCount}} Events",
      eventName: "Event Name",
      timestamp: "Timestamp",
      eventsCounter: "Event Counters",
      counterName: "Counter Name",
      count: "Count",
      description: "Description",
      eventType: {
        UNKNOWN: "Unknown event code",
        OVERCURRENT_MIL_SUB_MIN:
          "The import limit has been exceeded for more than 15s and less than 60s",
        OVERCURRENT_MEL_SUB_MIN:
          "The export limit has been exceeded for more than 15s and less than 60s",
        OVERCURRENT_MIL_TIMEOUT:
          "The import limit has been exceeded for more than 60s",
        OVERCURRENT_MEL_TIMEOUT:
          "The export limit has been exceeded for more than 60s",
        OVERCURRENT_145_PERCENT:
          "The imported current has exceeded 145% of the import limit",
        CUSTOMER_RESET: "The customer has reset the system",
        INSTALLER_RESET: "The manufacturer/installer has reset the system",
      },
      eventCounter: {
        EXPORT_COUNTER_10M:
          "Number of events that exceed the export limit for more than 15s within a rolling 10 minute window.",
        IMPORT_COUNTER_10M:
          "Number of events that exceed the import limit for more than 15s within a rolling 10 minute window.",
        EXPORT_COUNTER_24H:
          "Number of events that exceed the export limit for more than 15s within a rolling 24 hour window.",
        IMPORT_COUNTER_24H:
          "Number of events that exceed the import limit for more than 15s within a rolling 24 hour window.",
        CLS_30D_RESET_COUNTER:
          "Number of instances of the customer resetting their system due to a lockout within a rolling 30 day window.",
      },
    },
    rigData: {
      provisionedAt: "Provisioned At",
      lastPassTime: "Last Pass Time",
      blePin: "BLE Pin",
      pcbQr: "PCB QR",
      rigId: "Rig ID",
      uid: "UID",
      upc: "UPC",
      fpc: "FPC",
    },
    commissioning: {
      intro:
        "Displayed details were set at the time of commissioning and may have changed.",
      installation: "Installation",
      installer: "Installer",
      supplyAndLimits: "Supply & Limits",
      otherSettings: "Other Settings",
      createdOn: "Created On",
      isComplete: "Is Complete",
      isPaired: "Is Paired",
      masterDeviceType: "Master Device",
      masterDeviceSerialNumber: "Master Device Serial",
      hubDeviceType: "Hub Device",
      hubDeviceSerialNumber: "Hub Device Serial",
      hasBattery: "Has Battery",
      hasSolarPanel: "Has Solar Panel",
      hasWindTurbine: "Has Wind Turbine",
      hasHydroelectric: "Has Hydro Electric",
      hasElectricStorageHeating: "Has Electric Storage Heating",
      other: "Other",
      name: "Name",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      phoneNumber: "Phone Number",
      acceptedTermsOn: "Accepted Terms On",
      acceptedPrivacyOn: "Accepted Privacy On",
      appVersion: "App Version",
      importLimit: "Import Limit",
      earthingArrangement: "Earthing Arrangement",
      radioChannelId: "Radio Channel ID",
      mpan: "MPAN",
      pin: "Installer PIN",
    },
    deviceErrors: {
      title: "Error & Warning Log",
      firstOccurred: "First Occurred",
      lastOccurred: "Last Occurred",
      initiatedAt: "Initiated At",
      inverterCode: "Inverter Code",
      inverterDescription: "Inverter Description",
      inverterDiagnostics: "Inverter Diagnostics",
      myenergiCode: "myenergi Code",
      myenergiDescription: "myenergi Description",
      myenergiDiagnostics: "myenergi Diagnostics",
      noErrors: "No unresolved errors or warnings found.",
      errorsFound: "Unresolved errors or warnings found!",
    },
    zappi: {
      deviceType: "Zappi 1",
      id: "Id",
      chargeEnergy: "Charge Energy",
      pilotState: "Pilot State",
      zappiMode: "Zappi mode",
      pwm: {
        pwm: "PWM",
        rcdAc: "RCD AC",
        rcdDc: "RCD DC",
        rcdAcReturn: "RCD AC Return",
        zappiState: "Zappi State (ZSH)",
        rcdBoardState: "Zappi RCD Board State (ZSL)",
      },
    },
    harvi: {
      deviceType: "Harvi",
      id: "Id",
      serialNo: "Serial No",
      updatedAt: "Updated At",
      ctData: {
        id: "Id",
        ctNumber: "CT Number",
        type: "Type",
        current: "Current",
      },
    },
    ctData: {
      ctNumber: "CT Number",
      type: "Type",
      current: "Current",
    },
    ptData: {
      ptNumber: "PT Number",
      temp: "Temperature",
      setPoint: "Set Point",
      hyst: "Hyst",
      absent: "Absent",
    },
    warrantyChip: {
      extended: "Extended Warranty - {{date}}",
      standard: "Standard Warranty - {{date}}",
      expired: "Expired",
      notFound: "Missing customer or warranty information",
    },
    ocpp: {
      historyTitle: "History",
      chargingProfilesTitle: "Charging Profiles",
      settings: {
        seconds: "Sec",
        title: "OCPP Settings",
        ocppEnabled: "OCPP Enabled",
        status: "Status",
        platform: "EV Management Platform",
        backendUri: "Backend URI",
        chargeboxId: "ChargeBox ID",
        username: "Username",
        password: "Password",
        AuthorizationKey: "Authorization Key",
        HeartbeatInterval: "Heartbeat Interval",
        ClockAlignedDataInterval: "Clock Aligned Data Interval",
        ConnectionTimeOut: "Connection TimeOut",
        LocalAuthorizeOffline: "Local Authorize Offline",
        LocalPreAuthorize: "Local Pre Authorize",
        MeterValuesAlignedData: "Meter Values Aligned Data",
        MeterValuesSampledData: "Meter Values Sampled Data",
        MeterValueSampleInterval: "Meter Value Sample Interval",
        NumberOfConnectors: "Number Of Connectors",
        ResetRetries: "Reset Retries",
        ConnectorPhaseRotation: "Connector Phase Rotation",
        StopTransactionOnEVSideDisconnect:
          "Stop Transaction On EV Side Disconnect",
        StopTransactionOnInvalidId: "Stop Transaction On Invalid Id",
        StopTxnAlignedData: "Stop Txn Aligned Data",
        StopTxnSampledData: "Stop Txn Sampled Data",
        SupportedFeatureProfiles: "Supported Feature Profiles",
        TransactionMessageAttempts: "Transaction Message Attempts",
        TransactionMessageRetryInterval: "Transaction Message Retry Interval",
        UnlockConnectorOnEVSideDisconnect:
          "Unlock Connector On EV Side Disconnect",
        LocalAuthListEnabled: "Local Auth List Enabled",
        LocalAuthListMaxLength: "Local Auth List Max Length",
        SendLocalListMaxLength: "Send Local List Max Length",
        OCPPChargeMode: "OCPP Charge Mode",
        RandomSmartChargeDelay: "Random Smart Charge Delay",
        FreeCharging: "Free Charging",
        PlugAndChargeID: "Plug And Charge ID",
        CommercialMode: "Commercial Mode",
        MaintenanceMode: "Maintenance Mode",
        HasPaymentTerminal: "Has Payment Terminal",
        FreeVendScreen: "Free Vend Screen",
        PricekWh: "Price kWh",
        PriceMinute: "Price Minute",
        IdleFee: "Idle Fee",
        ConnectionFee: "Connection Fee",
        TariffType: "Tariff Type",
        AppName: "App Name",
        SupportContact: "Support Contact",
        IsOperative: "Is Operative",
        PaymentURL: "Payment URL",
        Currency: "Currency",
        CurrencyAfterValue: "Currency After Value",
        AllowPINCode: "Allow PIN Code",
        ChargerName: "Charger Name",
        ChargerID: "Charger ID",
      },
      chargingProfiles: {
        title: "Charging Profiles",
        table: {
          chargingProfileId: "Charging Profile ID",
          transactionId: "Transaction ID",
          slots: "Zappi slot ids",
          stackLevel: "Stack level",
          profilePurpose: "Profile Purpose",
          profileKind: "Profile Kind",
          recurrencyKind: "Recurrency Kind",
          validFrom: "Valid From",
          validTo: "Valid to",
        },
        clear: {
          title: "Clear Charging Profile",
          description:
            "Please confirm you'd like to clear the charging profile",
        },
        details: {
          title: "Charging Profile Schedule Periods",
          startSchedule: "Start Schedule",
          startPeriod: "Start Period",
          limit: "Limit",
          numberPhases: "Number of Phases",
          profilePurpose: "Profile Purpose",
          recurrencyKind: "Recurrency Kind",
        },
      },
      history: {
        title: "OCPP Log messages",
        timestamp: "Timestamp",
        messageType: "Message Type",
        origin: "Origin (Zappi/Central system)",
        message: "Message",
        from: "From",
        till: "Till",
        clear: "Clear",
      },
      warnings: {
        title: "Note Worthy Events",
        authorize: "Authorization event",
        remoteStartTransaction: "Remote Start Transaction",
        remoteStopTransaction: "Remote Stop Transaction",
        startTransaction: "Start Transaction",
        statusNotification: "Status Notification",
        stopTransaction: "Stop Transaction",
      },
    },
  },
  usersPage: {
    title: "Users",
    accountTitle: "User Account",
    createTitle: "Create {{userType}}",
    userType: {
      admin: "Administrator",
      seniorTechSupport: "Senior Technical Support",
      juniorTechSupport: "Junior Technical Support",
      aggregatorUser: "Aggregator User",
    },
    userList: {
      lastLogin: "Last login {{time}}",
      lastLoginDefaultValue: "unkown",
    },
    rights: {
      title: "User Roles",
      save: "Save",
      reset: "Reset",
      successfulUpdateMessage: "User Role permissions successfully updated!",
      aggregatorAssignedDevicesView: "Aggregators - View Devices",
      aggregatorManagement: "Aggregators - Manage / Edit",
      aggregatorView: "Aggregators - View Menu / Page",
      fleetApiView: "Fleet API - View Menu / Page",
      fleetApiManagement: "Fleet API - Manage / Edit",
      customerView: "Customers - View Menu / Page",
      customerLiveView: "Customers - LiveView Myaccount",
      customerEdit: "Customers - Manage / Edit",
      deleteCustomer: "Customers - Delete",
      deleteMyAccountDevices: "Customers - Delete MyAccount Devices",
      deleteInitialCommands: "Device - Clear Commands",
      dashboardView: "Dashboard - View Menu / Page",
      dashboardUpdateBoxManagement: "Dashboard - Manage / Update boxes",
      deviceBlock: "Device - Block Device",
      deviceConfigEdit: "Device Config - Edit",
      deviceConfigView: "Device Config - View",
      deviceDelete: "Device - Delete Device",
      deviceSetFirmware: "Firmware - Set/assign firmware to devices",
      firmwareManagement: "Firmware - Manage / Edit",
      firmwareView: "Firmware - View",
      firmwareRelease: "Firmware - Manage Releases",
      firmwarePrerelease: "Firmware - Manage Prereleases",
      hubDeregister: "Hubs - Deregister Hub",
      chargeLogsRead: "Charge Logs - Read - Not Implemented",
      hubDetailsView: "Hubs - Details View",
      hubMonitor: "Hubs - Auto Refresh - Not Implemented",
      hubMonitoring: "Hubs - Monitoring - Not Implemented",
      hubView: "Hubs - View Menu / Page",
      userManagement: "Users - Manage / Edit",
      userView: "Users - View Menu / Page",
      manageServers: "Servers - Manage",
      manageInstallers: "Installers - Manage / Edit",
      downloadBootloader: "Device - Download Bootloader",
      forceReboot: "Device - Force Reboot",
      forceDownload: "Device - Force Download",
      forceBackgroundDownload: "Device - Force Firmware Download",
      forceInstallApp: "Device - Force Firmware Install",
      forceInstallBootloader: "Device - Force Bootloader Install",
      g100Reset: "Device - G100 Reset",
      integratorView: "Integrators - View Menu / Page",
      integratorManagement: "Integrators - Manage / Edit",
      accessManagementView: "Access Management - View",
      accessManagementEdit: "Access Management - Edit",
      ocppDeviceEdit: "Device - OCPP / Edit",
      ocppDeviceView: "Device - OCPP / View",
      mqttManagement: "MQTT - Manage",
      registrationDateEdit: "Device - Registration Date",
      goodwillWarrantyEdit: "Goodwill Waranty - Edit",
      deviceAccessGroupsEdit: "Device Access Groups - Edit",
      deviceAccessGroupsView: "Device Access Groups - View",
    },
    create: {
      createButton: "Create Account",
      errorMessages: {
        required: "Required field.",
        invalidEmail: "Invalid email address format.",
      },
      successMessage: "Account successfully created!",
    },
    userManagement: {
      breadcrumb: "Users",
      accountDetails: "Account details",
      accountRemoval: "Account Removal",
      userRole: "User role",
      saveAccount: "Save Account",
      userNotFound: "No such user exist",
      deleteAccount: "Delete Account",
      deleteSuccessMessage: "User successfully deleted.",
      updateSuccessMessage: "User role successfully changed.",
      accountDeleteDescription:
        "Remove {{username}}'s account details from the myenergi Technical Support console.",
      deleteDialog: {
        title: "Delete User",
        details: "Are you sure you want to delete {{username}} user?",
        confirm: "ACCEPT",
        reject: "DECLINE",
      },
    },
  },
  dashboardPage: {
    title: "Dashboard",
    content: {
      numberOfHubs: "Hubs",
      numberOfZappis: "Zappis",
      numberOfEddis: "Eddis",
      numberOfLibbis: "Libbi controllers",
      numberOfLibbisInverters: "Libbi inverters",
      numberOfHarvis: "Harvis",
      live: "Live",
      addedYesterday: "Added Yesterday",
      addedLastWeek: "Added Last Week",
      activeAppUsers: "Active App Users",
    },
    updateBox: {
      title: "Title",
      description: "Description",
      cancel: "Cancel",
      save: "Save",
      errorMessages: {
        titleTooLong: "Title is too long",
        titleRequired: "Title cannot be empty",
        contentTooLong: "Content is too long",
      },
      currentPosts: "Notes",
      scheduledPosts: "Scheduled Notes",
    },
  },
  serversPage: {
    title: "Servers",
    table: {
      active: "Active",
      serverName: "Server Name",
      shard: "Shard",
      ip: "IP",
      url: "URL",
    },
    alternateServerBox: {
      set: "Set",
    },
    successfulUpdateMessage: "Server(s) successfully updated!",
  },
  search: {
    couldntFind:
      "Could not find any Hub or Device by the given Serial Number: {{searchTextValue}}",
    noHubConnected:
      "Device found: {{searchTextValue}}, but the device is not connected to a hub anymore.",
    invalidSerialNo: "The Serial Number is in an invalid format.",
    tooShortSearchField: "The search value must be at least 3 characters long",
    couldntFindCustomer:
      "Could not find any customers by the given search: {{searchTextValue}}",
    couldntFindFriendlyName:
      "Could not find any friendlyName by the given search: {{searchTextValue}}",
    serverError: "Server failed to respond for this search",
    generalPlaceholder: "Search by customer details or serial number",
    customerSelectTools: "Customer select tools",
    dsrPlaceholder:
      "Search by aggregator name, control group name, or serial number",
    userPlaceholder: "Search users by name or email",
    installerPlaceholder: "Search installers by email",
    accessManagementPlaceholder: "Search by user",
    accessGroupPlaceholder: "Search by friendly name",
    searchResults: "Search Results",
    searchNoResults: "No results found.",
    noUsersFound: "No users found.",
    clearSearchFilters: "Clear search parameters and filters",
    csvDownload: {
      exportSelectedToCsv: "Export selected to CSV",
      warning: "!! Caution !!!",
      warningMessage:
        "The list of customer data that you are about to export is covered by Data Protection Regulations as it includes personal information. \n" +
        "The data must be managed carefully - do not make additional copies or share it with others unless they are also aware of the Data Protection implications.\n" +
        "The data must not be stored on an insecure device (such as a USB stick) or shared outside myenergi.\n" +
        "The file must also be deleted as soon as the work on the data has been completed.\n" +
        "\n" +
        "Do you wish to proceed with the data export?",
      generateData: "Generate Data",
    },
  },
  customerPage: {
    title: "Customers",
    customer: "Customer",
    notFound: "Customer not found!",
    customerDetails: "Customer Details",
    myEnergiProducts: "Products",
    personalDetails: "Personal Details",
    thirdPartyConnections: "Third Party Connections",
    firstName: "First Name",
    lastName: "Last Name",
    mobileCode: "Mobile code",
    mobileNumber: "Mobile Number",
    landlineCode: "Landline code",
    landlineNumber: "Landline Number",
    accountType: "Account Type",
    emailAddress: "Email Address",
    emptyTable: "No customers to show at the moment",
    productLocation: "Location",
    productAddress: "Address",
    productAddressLine1: "Address Line 1",
    productAddressLine2: "Address Line 2",
    productAddressCity: "City",
    productAddressRegion: "Region",
    productAddressPostalCode: "Postal Code",
    productAddressRegionCountry: "Country",
    energyProvider: "Energy Provider",
    provider: "Provider",
    tariff: "Tariff",
    friendlyName: "Friendly Name",
    notAvailable: "(Not available)",
    productGateway: "Gateway",
    productDevices: "Connected Devices",
    disconnectedProductDevices: "Disconnected Devices",
    deletedDevices: "Deleted Devices",
    noDeletedDevices: "No deleted devices found.",
    disconnectedStatus: "Disconnected",
    onlineStatus: "Online",
    offlineStatus: "Offline",
    deletedStatus: "Deleted",
    reconnect: "Reconnect",
    standardWarrantyUntil: "Standard warranty until {{date}}",
    extendedWarrantyUntil: "Extended warranty until {{date}}",
    goodWillWarranty: "GoodWill warranty from",
    registrationDate: "Registration date",
    installDate: "Installed on {{date}}",
    warrantyExpired: "Warranty expired",
    warrantyCancelButton: "Cancel extended warranty",
    warrantyCancelled: "EXW cancelled {{date}}",
    productNotFound: "Product not found!",
    noLocation: "No location available.",
    dsrFlexOptions: "DSR Flex Options",
    integrations: "Integrations",
    optIn: "Opt in",
    optOut: "Opt out",
    deviceType: {
      hub: "Hub",
      eddi: "Eddi",
      eddiplus: "Eddi+",
      zappi: "Zappi",
      rapidzappi: "Zappi GLO",
      rapidzappiplus: "Zappi GLO+",
      zappi3: "Zappi 3",
      libbi: "Libbi",
      harvi: "Harvi",
    },
    table: {
      select: "Select",
      hubSerialNos: "Hub serial numbers",
      fullName: "Customer name",
      email: "Email",
      phoneNumber: "Phone number",
      postalCode: "Postal code",
      address: "Address",
    },
    customerForm: {
      editButton: "Edit",
      deleteButton: "Delete",
      saveButton: "Save",
      cancelButton: "Cancel",
      requiredMessage: "This field is required.",
      notAllowedToChange: "Can't be changed",
      confirmDeletePopup: {
        title: "Warning",
        content:
          "Are you sure that you want to delete the customer account {{email}}? This action is irreversible!",
      },
    },
    connections: {
      clientId: "Client ID",
      clientName: "Client Name",
    },
    optOutCustomersModal: {
      title: "Opt out customers",
      content:
        "This will opt out all selected customers from DSR, and will be removed from the allocated aggregator. Are you sure you want to continue?",
    },
    optedInDSR: "Opted In to Flex DSR:",
    activatedDSR: "Activated for Flex DSR:",
    cancelExtendedWarrantyPopUp: {
      title: "End Extended Warranty?",
      textRemaingCoolingOfPeriod:
        "There are {{numberOfDays}} days left of this cooling off period. Do you want to cancel this Extended Warranty?",
      textExpiredCoolingOfPeriod:
        "The cooling off period for this Extended Warranty has expired. Do you still want to cancel the Extended Warranty?",
      confirm: "Yes",
      cancel: "Close",
    },
    modifyWarrantyDialog: {
      replacementId: "Replacement device",
      oldSerialNo: "Old device serial number",
      currentSerialNo: "Current device serial number",
      startDate: "Start date",
      endDate: "End date",
      endDateHelperText:
        "Calculated based on start date and including {{years}} goodwill warranty.",
      warrantyTitle: "Warranty",
      extendedWarranty: "Extended warranty",
      standardWarranty: "Standard warranty",
    },
    goodwillWarranty: {
      title: "Goodwill warranty",
      intro: "Extend the warranty of the device through a goodwill gesture.",
      startDate: "Start date",
      startDateRequired: "Goodwill warranty start date is required.",
      reason: "Reason for goodwill warranty",
      reasonRequired: "A reason for goodwill warranty is required.",
      years: "Years",
      months: "Months",
    },
    modifyLibbiDialog: {
      controllerSerialNo: "Controller serial number",
      noPermission: "No permission to edit!",
      error: {
        inverterSerialNoRequired: "Inverter serialNo is required!",
        inverterLength: "Inverter serialNo must have 21 characters!",
        inverterStartsWith: "Inverter serialNo must start with a 2102!",
        batteryLength: "Battery serialNo must have 21 characters!",
        batteryStartsWith: "Battery serialNo must start with a 6106!",
        warrantyStartDateRequired: "Warranty start date is required",
        serialNoDigitsOnly: "The serialNo must contain only digits!",
      },
    },
    changeRegistrationDateDialog: {
      deviceSerialNo: "Device Serial Number",
      error: {
        registrationStartDateRequired: "Registration start date is required!",
      },
    },
    deviceDeletePopUp: {
      text: "Do you want to delete the {{deviceName}} with serial number {{selectedDeviceSerialNo}}? The device may be reconnected later.",
    },
    hardDeletePopUp: {
      title: "WARNING!!",
      text: "Do you want to PERMANENTLY DELETE the {{deviceName}} with serial number {{selectedDeviceSerialNo}}? This is NOT RECOVERABLE!",
    },
    successMessages: {
      updateCustomer: "Customer data was successfully updated",
      deleteCustomer: "Customer account was successfully deleted!",
      cancelWarranty: "Warranty was successfully cancelled",
      updateWarrantyDate: "Warranty date was successfully updated",
      replaceDevice: "Device successfully replaced",
      deleteReplacement: "Replacement successfully deleted",
      deleteDevice: "Device successfully deleted",
      deleteHub: "Hub successfully deleted",
      reconnectedDevice: "Device successfully reconnected to hub",
      optedOutOfDSR: "Customer was opted out of Flex DSR",
      libbiComponentsUpdate: "Libbi components successfully updated",
      goodwillWarrantyUpdated: "Goodwill warranty successfully updated",
    },
    errorMessages: {
      fetchCustomerData: "Couldn't fetch customer data",
      updateCustomer: "Couldn't update customer data",
      deleteCustomer: "Couldn't delete customer account!",
      warrantyDataChangeFailure: "Can't update both fields at the same time",
      noResultsReturned: "No results returned",
      libbiComponentsUpdate: "Could not update libbi components",
    },
  },
  aggregatorPage: {
    aggregator: "Aggregator",
    aggregators: "Aggregators",
    aggregatorId: "Aggregator Id",
    aggregatorGroupId: "Aggregator Id",
    aggregatorGroupName: "Aggregator name",
    aggregatorGroup: "Aggregator",
    aggregatorClientId: "Client Id",
    aggregatorDevices: "Devices",
    controlGroupNotFound: "Control group with id: {{id}} does not exist",
    aggregatorNotFound: "Aggregator with id: {{id}} does not exist",
    controlGroup: "Control Group",
    controlGroups: "Control Groups",
    controlGroupId: "Control group Id",
    controlGroupIds: "Control group Id(s)",
    deviceCount: "Number of devices",
    history: "History",
    createdAt: "Date of entry",
    dsrHealth: "DSR API health:",
    controlGroupDevices: "Control group Devices",
    addAllOptedInDevices: "Add all opted in devices",
    hubSerialNo: "Hub Serial Number",
    deviceSerialNo: "Device Serial Number",
    emptyTable: "No data to show at the moment",
    documentation: "Documentation",
    createAggregatorWarning:
      "!WARNING! These fields are confidential credentials, and it is only visible now.\n Only share these credentials with the aggregator.\n If credentials are lost, only DEVOPS team can reset them!",
    noDataReceived: "No data received",
    mainPage: {
      aggregatorButtonHelperText: "Aggregator and control group management",
      customerButtonHelperText: "Customer DSR activate/deactivate",
      eventHistoryButtonHelperText:
        "Dsr commands issued for groups and devices",
      groupHistoryButtonHelperText: "Group history of device Insert/Delete",
      documentationButtonHelperText: "Online documentation for the DSR api",
    },
    customerControl: {
      title: "DSR Customer manager",
      actions: "DSR Customer actions",
      optedInDsr: "Opted in customers:",
      activatedInDsr: "Activated customers:",
      customersInGroups: "Customers in DSR groups:",
      flexOptIn: "FlexOptIn",
      flexActive: "FlexActive",
      activateAndAllocateTitle:
        "Activate and allocate customers to an aggregator",
      numberOfCustomersHelperText: "Number of customers to allocate",
      aggregatorNameHelperText:
        "Name of the aggregator you want to add the activated customers",
      showDetails: "Details",
      activateAndAllocate: "Activate and allocate",
      successfullyActivated: "Customers were successfully activated: ",
      failedToActivate: "Customers failed to activate: ",
      requestResult: "Request results",
      activateAndAllocateSuccess:
        "Customers were successfully activated and allocated",
      activateAndAllocateErrorNoCustomers:
        "No customers were activated and allocated",
      someCustomersNotActivated:
        "Some customers were not activated and allocated",
    },
    aggregatorDetailsPanel: {
      details: "Details",
      error: "An error occurred while fetching the aggregator details.",
      notAggregated: "Device is not aggregated.",
      notInControlGroup: "Device not in control group.",
      dsrGroupInfo: "DSR group info",
      groupInfo: "Group info",
      groupHistory: "Group history",
      loadControl: "Load Control",
      frequencyResponse: "Frequency Response",
      frequencyStatus: "Configured",
      activeDsr: "Active",
      load: "Set load",
      seconds: "Remaining time",
      csn: "Sequence no.",
      ack: "Command ack",
      lce: "Load control enabled",
      lca: "Load control active",
      mainsFrequency: "Mains frequency",
      frTurnUp: "Frequency turn UP",
      frTurnDown: "Frequency turn DOWN",
      frEnabled: "Enabled",
      frActive: "Active",
      frNow: "Now",
      daf: "Turn DOWN activate frequency (DAF)",
      ddf: "Turn DOWN deactivate frequency (DDF)",
      uaf: "Turn UP activate frequency (UAF)",
      udf: "Turn UP deactivate frequency (UDF)",
      generatorPower: "Generator power",
      gridPower: "Grid power",
    },
    historyCommon: {
      filter: "Filter",
      filterType: "Filter by",
      filterParameter: "Filter parameter",
      ttl: "Time to live (min)",
      clearFilter: "Clear filter",
    },
    eventHistory: {
      title: "Event history",
      command: "Command",
      dsrEventId: "DSR event ID",
      dsrCommandId: "DSR command ID",
      commandType: "Type",
      commandState: "State",
      commandInitiated: "Initiated",
      commandLastSent: "Last sent",
      commandTries: "Tries",
      eventEffectiveness: "Effectiveness",
      baselinePower: "Baseline Power",
      eventDurationInMinutes: "Actual Duration",
      numberOfDevices: "Number of Devices",
      dsrSetPoint: "DSR Target Power",
      targetPowerDelta: "Target Power Delta",
      targetEnergyDelta: "Target Energy Delta",
      actualEnergyDelta: "Actual Energy Delta",
      dsrEffectivenessFactor: "Effectiveness",
      calculationError: "Calculation",
      historyCoverage: "Coverage of historic minute data",
      commandRatios: "Command Ratios",
      controlledDeviceCount: "Controlled Device Count",
      targetDeviceCount: "Target Device Count",
      availabilityRatio: "Availability Ratio",
      numOfCommandsSent: "Commands Sent",
      successRatio: "Command Success Ratio",
      failureRatio: "Command Failure Ratio",
      commandsMissingError: "Commands are missing",
      devicesThatTookAction: "Show devices that took action",
      commandRelatedToEvent: "Show commands related to event",
    },
    groupHistory: {
      title: "Group History",
      action: "Action",
      insertAdminGroup: "Insert to aggregator",
      deleteAdminGroup: "Removed from aggregator",
      insertControlGroup: "Insert to control group",
      deleteControlGroup: "Removed from control group",
    },
    groupHistoryPanel: {
      title: "DSR group history",
      emptyTable: "No history to show at the moment",
      error: "An error occurred while fetching the group history",
      createdAt: "Date of entry",
      updatedAt: "Updated at",
      actionType: "Action type",
      insert: "Insert",
      update: "Update",
    },
    successMessages: {
      addDevice: "Device successfully added to group",
      addCustomers: "Customers successfully added to aggregator",
      removeDevice: "Device successfully removed from group",
      create: "{{entity}} was successfully created",
      update: "{{entity}} was successfully updated",
      delete: "{{entity}} was successfully deleted",
    },
    errorMessages: {
      addDevice: "Couldn't add device to group {{error}}",
      addDeviceFailed: "Couldn't add devices to aggregator",
      addDeviceFailedAndAdded: "Couldn't add ALL devices to aggregator",
      addCustomers: "Couldn't add customers to aggregator",
      removeDevice: "Couldn't remove device from group",
      general: "Something went wrong",
      dsrDown: "DSR service not available",
      notImplemented: "Not implemented yet",
      fetchDsr: "Couldn't fetch dsr data",
      create: "{{entity}} create failed",
      update: "{{entity}} update failed",
      delete: "{{entity}} delete failed",
      fetchAggregators: "Couldn't fetch aggregators",
      eventNotFound: "Event not found",
      commandNotFound: "Command not found",
      hubNotFound: "Hub not found",
    },
    deleteDialog: {
      title: "Delete {{group}}",
      aggregator:
        "Are you sure you want to delete the {{aggregatorName}}?. This will delete it's group, remove devices from this aggregator, and delete from user pool.",
      details: "Are you sure you want to delete the {{groupName}} {{group}}?",
    },
    removeDialog: {
      title: "Remove device",
      details:
        "Are you sure you want to remove the serialNo: {{serialNo}} device from the group?",
    },
    addDialog: {
      title: "Add device",
      details: "Provide serialNo for adding device to group",
    },
    addOptedInDialog: {
      title: "Add all opted in devices",
      details:
        "Are you sure you want to add all available devices to the group?\nThis will add {{affectingUsers}} user's all device's to the aggregator({{affectingHubs}} hub's affected).\n Only users are opted in to the Dsr services will be added.",
    },
    createDialog: {
      title: "Create {{group}}",
      details: "Provide name for new {{group}}",
    },
    tooltip: {
      addDevice: "Add device",
      removeDevice: "Remove device",
      delete: "Delete {{group}}",
      create: "Create {{group}}",
      edit: "Edit {{group}}",
      rest: "Options",
      refresh: "Refresh {{entity}}",
    },
    updateDialog: {
      title: "Update {{group}}",
      details: "Please provide new name for {{groupName}} {{group}}",
    },
  },
  installersPage: {
    title: "Installers",
    addInstaller: "Add Installer",
    editInstaller: "Edit Installer",
    addInstruction: "Here you can add a new installer.",
    editInstruction: "Here you can edit the existing installer.",
    deleteInstaller: "Delete Installer",
    deleteInstallerInstruction:
      "Are you sure you want to delete installer {{email}} ?\n This action is NOT reversible.",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    enabledHeader: "Enabled",
    createdDate: "Created date",
    lastModified: "Last modified",
    messageActionSendNotification: "Send welcome email",
    messageActionResendNotification: "Send reset password email",
    emailVerified: "Email verified",
    emailNotVerified: "Email not yet verified",
    emptyTable:
      "No installers found with email starting with {{searchTerm}}. Please change the search and try again.",
    loadInstallersError: "Failed to load installers list.",
    enabled: "enabled",
    disabled: "disabled",
    actionError: "Failed to {{action}} {{email}}",
    actionSuccess: "Installer {{email}} {{action}} successfully.",
    genericError: "Command failed.",
  },
  firmware: {
    title: "Firmware",
    manageFirmware: "Manage Firmware",
    productCode: "Product Code",
    deviceName: "Device Name",
    delivery: "Delivery Platform",
    channel: "Channel",
    alpha: "Alpha",
    beta: "Beta",
    ga: "GA",
    gaUnapproved: "GA (Not yet approved)",
    generalAvailability: "General Availability",
    manageChannels: "Manage Release Channel Devices",
    uploadFirmware: "Upload New Firmware",
    started: "Started",
    ended: "Ended",
    jobID: "Job ID",
    lastUpdated: "Last Updated",
    target: "Target",
    succeeded: "Succeeded",
    queued: "Queued",
    inProgress: "In Progress",
    failed: "Failed",
    rejected: "Rejected",
    releaseJobs: "Release Jobs",
    firmwareImages: "Firmware Images",
    filename: "Filename",
    version: "Version",
    created: "Created",
    size: "Size",
    checksum: "Checksum",
    approvalState: "Approval State",
    notes: "Notes",
    noData: "No data available at the moment.",
    end: "End",
    approve: "Approve",
    withdraw: "Withdraw",
    unwithdraw: "Unwithdraw",
    release: "Release",
    addDevice: "Add/Move Device",
    deviceUID: "Device UID",
    setByEmail: "Set by email",
    setChannelSuccess:
      "Successfully assigned {{device}} to the {{channel}} channel.",
    modalsCommon: {
      confirmExplain: "Type the firmware version below to confirm:",
      confirmVersion: "Confirm Version",
      reason: "Reason",
    },
    endModal: {
      title: "End Release",
      details:
        "Are you sure you want to end the {{deviceName}} / {{channel}} / {{target}} release job?",
      successMessage:
        "Release job {{deviceName}} / {{channel}} / {{target}} ended successfully.",
    },
    deleteModal: {
      title: "Delete {{channel}} Device",
      details:
        "Are you sure you want to delete the device with serial number {{target}} from the {{channel}} channel? It will be moved to the GA channel.",
    },
    uploadModal: {
      title: "Upload Firmware",
      intro: "Upload firmware for {{deviceName}} devices.",
      fileName: "Firmware Binary",
      md5Base64: "md5Base64",
      md5Info:
        "The md5Base64 will be used to verify the integrity of the file upload.",
      select: "Select...",
      fileUploadSuccess: "The file {{fileName}} was successfully uploaded.",
      fileUploadFail: "Failed to upload the firmware file {{fileName}}",
    },
    approveModal: {
      title: "Approve Firmware {{version}}",
      intro:
        "Approving a firmware version makes it available to release to GA and also for TSEs to install on production customer devices even if not yet released to GA (useful if a bug fix needs to be tested on an individual customer).",
      releaseNotes: "Release Notes",
      successMessage: "Approved firmware file {{fileName}} successfully.",
    },
    withdrawModal: {
      title: "Withdraw Firmware {{version}}",
      intro:
        "Withdrawing a firmware will make it unavailable for release to any channel or directly via TSE.",
      successMessage: "Withdrawn firmware file {{fileName}} successfully.",
    },
    unwithdrawModal: {
      title: "Unwithdraw Firmware {{version}}",
      intro:
        "Unwithdrawing makes the firmware available again for release to pre-release channels.",
      successMessage: "Unwithdrawn firmware file {{fileName}} successfully.",
    },
    releaseModal: {
      title: "Release Firmware {{version}}",
      intro:
        "Releasing a firmware channel will start uploading the firmware to devices assigned to the channel.",
      channelRequired: "At least one channel is required.",
      rolloutOptions: "Advanced rollout config options",
      maximumPerMinute: "Max per minute",
      exponentialRate: "Exponential rate",
      baseRatePerMinute: "Base rate per minute",
      incrementFactor: "Increment factor",
      rateIncreaseCriteria: "Rate increase criteria",
      numberOfNotifiedThings: "No. of notified things",
      numberOfSucceededThings: "No. of succeeded things",
      wrongVersion: "Version must be {{version}}",
      successMessage:
        "Firmware release for file {{fileName}} for channel(s) {{channels}} was successfull.",
    },
    addDeviceModal: {
      title: "Move Device to Release Channel",
      intro:
        "Assign a device to a specific release channel. Useful if a bug fix needs to be tested on an individual customer.",
      serialNo: "Serial Number",
      serialNoTypeError: "Serial Number must be a number.",
    },
  },
  measurementUnits: {
    watt: "{{count}} W",
    kilowatt: "{{count}} kW",
    kilowattHour: "{{count}} kWh",
    wattMinutes: "{{count}} Wmin",
    minutes: "{{count}} minute",
    minutes_plural: "{{count}} minutes",
    hertz: "{{count}} Hz",
  },
  thirdPartyApiPage: {
    integrators: "Integrators",
    documentation: "Documentation",
    emptyTable: "No data to show at the moment",
    deviceCount: "Number of devices",
    mainPage: {
      integratorsButtonHelperText: "Integrator management",
      documentationButtonHelperText:
        "Online documentation for the 3rd party api",
    },
    tooltip: {
      refresh: "Refresh {{entity}}",
      createIntegrator: "Add integrator",
      editIntegrator: "Edit integrator",
      removeIntegrator: "Delete integrator",
      rotateClientSecret: "Re-generate client secret",
      addUriButtonText: "Add new redirect uri",
      removeUriText: "Remove redirect uri from integrator",
    },
    addDialog: {
      title: "Add integrator",
      addUriPlaceholderText: "Enter new uri and click the + button",
      applyButtontext: "Apply Changes",
    },
    editDialog: {
      title: "Edit integrator",
      addUriPlaceholderText:
        "Enter new uri, click the + button, and apply changes if ready",
      applyButtontext: "Apply Changes",
    },
    removeDialog: {
      title: "Delete integrator",
      details:
        "Are you sure you want to permanently delete {{name}} integrator?",
      confirm: "Please enter DELETE to enable:",
    },
    rotateSecretDialog: {
      title: "Rotate client secret",
      details: "Are you sure you want to regenerate the client secret?",
      confirm: "Please enter CONFIRM to enable:",
    },
    copySecretDialog: {
      title: "New client secret",
      details:
        "This is a newly generated client secret for {{clientName}}. You can copy it by clicking on the icon. Once you have closed this window you won't be able to see the secret again!",
      copySuccess: "Successfully copied to clipboard",
      confirm:
        "Please enter CONFIRM that you have copied and saved the secret:",
    },
    successMessages: {
      addIntegrator: "Integrator successfully added.",
      updateIntegrator: "Integrator data successfully updated.",
      deleteIntegrator: "Integrator successfully deleted.",
    },
    warningMessages: {
      uriToAdd:
        "Please add the url(s) by clicking on the + button, and then submit.",
    },
    errorMessages: {
      getIntegrators:
        "Couldn't get integrators! (Probably you're not authorized.)",
      addIntegrator: "Couldn't add integrator with the name {{name}}!",
      fetchThirdPartyApis: "Couldn't fetch integrators data",
      fetchIntegrator: "Couldn't fetch integrator data",
      updateIntegrator: "Couldn't update {{name}} integrator!",
      deleteIntegrator: "Couldn't delete {{name}} integrator!",
      rotateSecret: "Couldn't rotate the client secret!",
      operationPermissionDenied:
        "Couldn't perform operation! (Probably you're not authorized.)",
      notAuthorized: "Not authorized!",
      deleteThresholdExceeded:
        "The number of devices ({{numberOfDevices}}) has exceeded the safe limit for deletion ({{threshold}}). To proceed, please reach out to the DevOps team for assistance.",
    },
  },
  accessManagementPage: {
    title: "Access Management",
    guestLocation: "Guest location",
    mainTable: {
      user: "User",
      location: "Location",
      mainAccessGroup: "Main Access Group",
      additionalAccessGroups: "Additional Access Groups",
      accessGroup: "Access Group",
      pins: "Pins",
      noDataText: "No data to show at the moment",
    },
    accesGroupTable: {
      friendlyName: "Friendly Name",
      defaultChargeMode: "Charge Mode",
      createdAt: "Created At",
      lastUsed: "Last Used",
      ocppTag: "OCPP Tag",
      usageType: "Usage",
      actions: "Actions",
    },
    moreButton: {
      addNewPin: "Add New Pin",
      addAdditionalAccesGroup: "Add Additional Access Group",
      addDevice: "Add Device",
      removeDevice: "Remove Device",
      removeAdditionalAccessGroup: "Remove Additional Access Group",
    },
    addNewpinDialog: {
      title: "Add a new PIN code",
      editTitle: "Edit PIN",
      friendlyName: "Friendly Name",
      pinCode: "Enter 5 digit PIN",
      changePin: "Change PIN code",
      usageType: "Usage type",
      chargeMode: "Default charge mode",
      businessUsage: "Business use",
      personalUsage: "Personal use ",
      validation: {
        friendlyNameLimit: "Maximum length for friendly name is 18 characters.",
        friendlyNameRequired: "Friendly name is required.",
        friendlyNameSpecialCharacter:
          "Friendly name must  not include special characters.",
        pinCodeDuplicate: "PIN code already exists.",
        pinCodeRequired: "PIN code is required",
        pinCodeRestriction: "PIN digits should be between 1&4",
        pinCodeMaxLength: "PIN code must be 5 digits",
        usageTypeRequired: "Usage type is required.",
      },
    },
    removePin: {
      title: "Remove Pin {{friendlyName}}",
      removeButton: "Remove pin",
      description1: "You're about to delete ",
      description2:
        ", the PIN will be deactivated the next time zappi is online.",
      description3: "Are you sure you want to do this?",
      description:
        "You're about to delete {{friendlyName}}, the PIN will be deactivated the next time zappi is online.",
    },

    addAdditionalAccessGroup: {
      title: "Add an additional group",
      accessGroupName: "Access Group Name",
      deviceSerialNoLabel: "Select devices",
      validation: {
        additionalAccessGroupNameRequired: "Access Group name is required",
        devicesSerialNoRequired: "At least one device is required",
      },
    },
    addDeviceDialog: {
      title: "Add a device to this group",
      label: "Enter Zappi Serial Number",
      validation: {
        deviceExists: "A device already exists with this SN",
        serialNumberRequired: "Serial number is required",
      },
    },
    removeDeviceDialog: {
      title: "Remove device",
      removeDevice: "Choose a device to remove",
    },
    removeAccessGroup: {
      removeButton: "Remove",
      title: "Remove Access Group",
      description:
        "Remember that all users in this group will revert back to being main access group users. WARNING: All history of this group will be lost!",
    },
  },
  ocppPage: {
    deviceSerialNo: "Serial Number",
    ocppPlatform: "OCPP Platform",
    allDevices: "All Devices",
    createdAt: "Created at",
    emptyTable: "No data to show at the moment",
    devicesConnected: "device connected",
    ocppDevices: "All devices",
    ocppPlatformDevices: "Devices by OCPP Platforms",
    clear: "Clear",
    from: "From",
    till: "Till",
    connectedDevices: "Connected devices",
    notEnoughData: "Not enough data to show",
    searchPlaceholder: "Search by Serial Number",
    filteredDevices: "Filtered devices",
    totalDevices: "Total devices",
  },
  mqttPage: {
    title: "MQTT",
    description:
      "Control which app versions can use MQTT for the various interactions, and restrict each version by product code and serial number",
    appVersionConfigs: {
      addButton: "Add App Version Config",
      emptyTable: "No app version configs to show at the moment",
      appVersion: "App Version",
      enabled: "Enabled",
      disabled: "Disabled",
      productCodesHeader: "Product codes",
      allowedSerialsHeader: "Allowed serials",
      allValue: "All",
      notConfigured: "Not configured",
      scopes: {
        rfidConfig: "RFID Config",
        deviceConfig: "Device Config",
        ocppConfig: "OCPP Config",
      },
    },
    modal: {
      addTitle: "Add App Version Config",
      editTitle: "Edit App Version Config ({{appVersion}})",
      intro:
        "Set which product codes and serial numbers each app version is allowed to use. Leave a field empty to allow all. Use the toggles to enable or disable each config scope.",
      appVersionLabel: "App Version",
      appVersionRequired: "App version is required.",
      duplicateAppVersion:
        "An app version config for {{appVersion}} already exists.",
      productCodesLabel: "Product Codes",
      productCodesHelperText: "Leave empty to allow all product codes.",
      allowedSerialsLabel: "Allowed Serials",
      allowedSerialsHelperText: "Leave empty to allow all serials.",
      enabledToggleAriaLabel: "Toggle {{scope}} enabled",
      successMessage: "Saved app version config {{appVersion}} successfully.",
      failedMessage: "Failed to save app version config {{appVersion}}.",
    },
    deleteModal: {
      title: "Delete App Version Config",
      intro:
        "Are you sure you want to delete the MQTT app version config for version {{appVersion}}? This cannot be undone.",
      successMessage: "Deleted app version config {{appVersion}} successfully.",
      failedMessage: "Failed to delete app version config {{appVersion}}.",
    },
  },
  gridpayNlPage: {
    title: "Gridpay NL",
    description:
      "Manage the devices eligible for the Gridpay Netherlands pilot. Uploading a CSV adds or updates devices; a device is removed from the pilot by uploading it as false, never by leaving it out of a later file.",
    table: {
      emptyTable: "No devices are on the Gridpay NL allowlist yet.",
      serialNo: "Serial number",
      status: "Status",
      updatedAt: "Last updated",
      enabled: "Enabled",
      disabled: "Disabled",
      filterLabel: "Status",
      filterAll: "All",
      filterEnabled: "Enabled",
      filterDisabled: "Disabled",
      upload: "Upload CSV",
      export: "Export current list",
      downloadTemplate: "Download template",
    },
    upload: {
      title: "Upload Gridpay NL allow-list",
      intro:
        "Upload a CSV with the columns 'serialNo' and 'enabled' to add or update devices. Devices are added to, and updated in the allow-list; Not removed. Disable a device to exclude it from the existing allow-list",
      fileName: "CSV file",
      select: "Select...",
      success: "Upload applied. {{count}} rows written to the allow-list",
      rejectedInfo:
        "Nothing was written. Fix the lines below and upload the file again.",
      rejectionLine: "Line {{line}}: {{reason}}",
      genericError: "The upload could not be completed. Please try again.",
    },
  },
  fleetApiPage: {
    title: "Fleet API",
    description:
      "Manage the clients that consume the Fleet API and the devices each of them is allowed to access.",
    clients: {
      title: "Clients",
      clientName: "Client name",
      clientId: "Client ID",
      apiAccess: "API access",
      deviceCount: "Devices",
      accessFleet: "Fleet",
      accessBoth: "Fleet and DSR",
      emptyTable: "No fleet API clients to show at the moment",
      loadFailed: "Failed to load fleet API clients.",
    },
    createClient: {
      button: "Create client",
      title: "Create fleet API client",
      intro:
        "Enter a name for the new client. A client ID and secret will be generated for it.",
      namePlaceholder: "Client name",
      successMessage: "Created fleet API client {{clientName}}.",
      duplicateMessage:
        "A fleet API client named {{clientName}} already exists.",
      failedMessage: "Failed to create the fleet API client.",
    },
    secretDialog: {
      title: "Client credentials",
      warning:
        "Copy the client secret now and pass it to the integrator securely. This is the only time it is shown — if it is lost, the only remedy is to create a new integrator.",
      clientId: "Client ID",
      clientName: "Client name",
      clientSecret: "Client secret",
      copy: "Copy secret",
      copied: "Client secret copied to the clipboard.",
      copyFailed: "Could not copy the client secret to the clipboard.",
    },
    detail: {
      title: "Client {{clientName}}",
      description:
        "The credentials this integrator authenticates with, the devices it may read and control, and every webhook delivery attempted to it.",
      backToClients: "Back to clients",
      scopes: "OAuth scopes",
      loadFailed: "Failed to load the fleet API client.",
      notFound: "That fleet API client could not be found.",
    },
    grants: {
      title: "Device grants",
      serialNo: "Serial number",
      deviceClass: "Device class",
      createdAt: "Granted at",
      emptyTable: "This client has no device grants",
      loadFailed: "Failed to load the device grants.",
    },
    addGrant: {
      button: "Add grant",
      title: "Add device grant",
      intro:
        "Grant this client access to a device. Granting a device it already holds makes no change.",
      serialNoLabel: "Serial number",
      serialNoInvalid: "Enter a valid device serial number.",
      serialNoUnknown: "No device was found with that serial number.",
      deviceClassLabel: "Device class",
      deviceClassLoading: "Looking up the device…",
      deviceClassNotGrantable:
        "{{deviceClass}} devices cannot be granted to a fleet client.",
      deviceClassLookupFailed:
        "The device class could not be looked up. Try again.",
      successMessage: "Granted {{serialNo}} to this client.",
      failedMessage: "Failed to grant {{serialNo}} to this client.",
    },
    revokeGrant: {
      button: "Revoke",
      title: "Revoke device grant",
      intro:
        "Revoke this client's access to {{serialNo}}? The client will immediately stop receiving data for that device.",
      successMessage: "Revoked {{serialNo}} from this client.",
      failedMessage: "Failed to revoke {{serialNo}} from this client.",
    },
    revokeAllGrants: {
      button: "Revoke all grants",
      title: "Revoke all device grants",
      intro:
        "Revoke every device grant held by {{clientName}}? This removes all of the client's device access and cannot be undone.",
      progress: "Revoked {{done}} of {{total}} grants…",
      noGrants: "This client holds no grants to revoke.",
      successMessage: "Revoked all {{total}} grants from {{clientName}}.",
      partialMessage:
        "Revoked {{done}} of {{total}} grants from {{clientName}}. Failed: {{failed}} — try again.",
      partialMessageMoreFailures: "{{failed}} and {{remaining}} more",
      failedMessage: "Failed to revoke the grants from {{clientName}}.",
    },
    deleteClient: {
      button: "Delete client",
      disabledTooltip:
        "Deleting a client is not yet supported. Revoke all grants to remove the client's access to every device.",
    },
    deviceClients: {
      tab: "Fleet API",
      title: "Fleet API clients",
      addButton: "Add to integrator",
      addTitle: "Add {{serialNo}} to a fleet integrator",
      addIntro:
        "The device is granted to the chosen integrator as a {{deviceClass}}. They can then read its status and control it through the Fleet API.",
      integrator: "Integrator",
      addedMessage: "Device added to the integrator.",
      addFailedMessage: "Failed to add the device to the integrator.",
      clientName: "Client name",
      clientId: "Client ID",
      emptyTable: "No fleet API clients have access to this device",
      loadFailed: "Failed to load the fleet API clients for this device.",
    },
  },
};

export default en;
